import { useState, useRef, useEffect, useCallback } from "react";
import { supabase } from "./supabase";
import AdminPanel from "./AdminPanel";
import { getHolidaysForYear, getMonthObservances } from "./holidays";

// ── Theme ──────────────────────────────────────────────
const C = {
  page:          "#f4f4f6",
  header:        "#4a4e5c",
  accent:        "#8a9bbf",
  accentDim:     "#a4b2cc",
  accentSoft:    "#e8ecf3",
  accentText:    "#dce4f0",
  highlight:     "#b6c2d4",
  highlightSoft: "#eef1f6",
  event:         "#a89bb5",
  border:        "#dcdfe8",
  card:          "#fafafa",
  headerCard:    "#eaecf1",
  dark:          "#2a2d38",
  mid:           "#52566a",
  muted:         "#8d919e",
  faint:         "#b8bcc8",
  holiday:       "#7aaa8a",
  todo:          "#9b8fb5",
};

// ── Date helpers ───────────────────────────────────────
const getSundayOfWeek = (date) => {
  const d = new Date(date); d.setDate(d.getDate()-d.getDay()); d.setHours(0,0,0,0); return d;
};
const getNextSunday   = (s) => { const d=new Date(s); d.setDate(d.getDate()+7); return d; };
const formatWeekLabel = (s) => new Intl.DateTimeFormat("en-US",{month:"long",day:"numeric",year:"numeric"}).format(s);
const formatShort     = (s) => new Intl.DateTimeFormat("en-US",{month:"short",day:"numeric"}).format(s);
const isSameDay       = (a,b) => a.getFullYear()===b.getFullYear()&&a.getMonth()===b.getMonth()&&a.getDate()===b.getDate();
const isInWeek        = (sun,date) => { const e=new Date(sun); e.setDate(e.getDate()+6); e.setHours(23,59,59); return date>=sun&&date<=e; };
const isInRange       = (start,end,date) => { const s=new Date(start); s.setHours(0,0,0,0); const e=new Date(end); e.setHours(23,59,59); return date>=s&&date<=e; };


const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const NOTE_CHIPS = ["Holiday","Travel","Special event","Collab week"];

const DEFAULT_IDEA_CATS = [
  {id:"c1",label:"Dyed",     color:"#b58a9b"},
  {id:"c2",label:"Reformed", color:"#8aadad"},
  {id:"c3",label:"Variety",  color:"#bfaa8a"},
  {id:"c4",label:"Other",    color:"#8d919e"},
];

const DEFAULT_EVENT_TAGS = [
  {id:"e1",label:"Travel",  color:"#8a9bbf"},
  {id:"e2",label:"Holiday", color:"#c8aa64"},
  {id:"e3",label:"Collab",  color:"#a89bb5"},
  {id:"e4",label:"Request", color:"#8aadad"},
  {id:"e5",label:"Other",   color:"#8d919e"},
];

const TAG_COLORS = ["#8a9bbf","#b58a9b","#a89bb5","#8aadad","#9b9bbf","#8aadbf","#bfaa8a","#8d919e","#a89b7a","#9baa8a"];

const ALL_COUNTRIES = [
  {code:"US",name:"United States"},{code:"GB",name:"United Kingdom"},
  {code:"IN",name:"India"},{code:"CA",name:"Canada"},
  {code:"AU",name:"Australia"},{code:"MX",name:"Mexico"},
  {code:"DE",name:"Germany"},{code:"FR",name:"France"},
  {code:"BR",name:"Brazil"},{code:"JP",name:"Japan"},
  {code:"NG",name:"Nigeria"},{code:"ZA",name:"South Africa"},
  {code:"PK",name:"Pakistan"},{code:"PH",name:"Philippines"},
  {code:"MY",name:"Malaysia"},{code:"CN",name:"China"},
  {code:"RU",name:"Russia"},{code:"TR",name:"Turkey"},
  {code:"BD",name:"Bangladesh"},{code:"IQ",name:"Iraq"},
  {code:"EG",name:"Egypt"},
];

const buildGrid = (year,month) => {
  const first=new Date(year,month,1),last=new Date(year,month+1,0),days=[];
  for(let i=0;i<first.getDay();i++) days.push({date:new Date(year,month,1-(first.getDay()-i)),thisMonth:false});
  for(let d=1;d<=last.getDate();d++) days.push({date:new Date(year,month,d),thisMonth:true});
  while(days.length<42){const n=new Date(days[days.length-1].date);n.setDate(n.getDate()+1);days.push({date:n,thisMonth:false});}
  return days;
};

// ── Shared components ──────────────────────────────────
const ColHeader = ({children,style}) => (
  <p style={{margin:0,fontSize:10,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"sans-serif",color:C.muted,...style}}>{children}</p>
);
const DashedBtn = ({onClick,children,color}) => (
  <button onClick={onClick} style={{background:"none",border:`1px dashed ${color||C.accent}`,borderRadius:8,padding:"10px 0",width:"100%",fontSize:13,fontFamily:"sans-serif",color:color||C.accent,cursor:"pointer",letterSpacing:0.5}}>{children}</button>
);
const SaveBtn = ({onClick,disabled,children}) => (
  <button onClick={onClick} disabled={disabled} style={{background:disabled?C.border:C.header,color:disabled?C.faint:C.accentText,border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontFamily:"sans-serif",cursor:disabled?"default":"pointer"}}>{children}</button>
);
const CancelBtn = ({onClick}) => (
  <button onClick={onClick} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 18px",fontSize:13,fontFamily:"sans-serif",color:C.muted,cursor:"pointer"}}>Cancel</button>
);

function EntryForm({week,updWeek,cancelDraft,saveEntry,draftRef}) {
  return (
    <div style={{padding:"14px 20px"}}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <input ref={el=>(draftRef.current[week.id]=el)} type="text" placeholder="Video name..." value={week.draftVideoName} onChange={e=>updWeek(week.id,{draftVideoName:e.target.value})}
          style={{width:"100%",border:"none",borderBottom:`2px solid ${C.accent}`,outline:"none",fontSize:15,color:C.dark,background:"transparent",fontFamily:"'Georgia',serif",padding:"6px 0",boxSizing:"border-box"}}/>
        <textarea placeholder="What do I need to prep..." value={week.draftPrep} onChange={e=>updWeek(week.id,{draftPrep:e.target.value})} rows={3}
          style={{width:"100%",border:"none",borderBottom:`2px solid ${C.border}`,outline:"none",resize:"none",fontSize:15,color:C.mid,background:"transparent",fontFamily:"'Georgia',serif",padding:"6px 0",boxSizing:"border-box"}}/>
      </div>
      <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
        <CancelBtn onClick={()=>cancelDraft(week.id)}/>
        <SaveBtn onClick={()=>saveEntry(week.id)} disabled={!week.draftVideoName.trim()&&!week.draftPrep.trim()}>Save</SaveBtn>
      </div>
    </div>
  );
}

function InlineEdit({value,onChange,multiline,style,placeholder}) {
  const [editing,setEditing] = useState(false);
  const [val,setVal]         = useState(value);
  useEffect(()=>{ if(!editing) setVal(value); },[value,editing]);
  if(!editing) return (
    <span onClick={()=>setEditing(true)} style={{cursor:"text",...style}}>
      {val||<span style={{color:C.faint,fontStyle:"italic"}}>{placeholder||"Tap to edit"}</span>}
    </span>
  );
  if(multiline) return (
    <textarea autoFocus value={val} onChange={e=>setVal(e.target.value)} onBlur={()=>{onChange(val);setEditing(false);}} rows={3}
      style={{width:"100%",border:"none",borderBottom:`2px solid ${C.accent}`,outline:"none",resize:"none",fontSize:"inherit",color:C.dark,background:"transparent",fontFamily:"inherit",padding:0,boxSizing:"border-box"}}/>
  );
  return (
    <input autoFocus type="text" value={val} onChange={e=>setVal(e.target.value)}
      onBlur={()=>{onChange(val);setEditing(false);}}
      onKeyDown={e=>{ if(e.key==="Enter"){onChange(val);setEditing(false);} }}
      style={{width:"100%",border:"none",borderBottom:`2px solid ${C.accent}`,outline:"none",fontSize:"inherit",color:C.dark,background:"transparent",fontFamily:"inherit",padding:"0 0 2px",boxSizing:"border-box"}}/>
  );
}

// ── App ────────────────────────────────────────────────
// ── IdeaCard component ─────────────────────────────────
function IdeaCard({idea, cats, expandedIdeas, toggleIdeaExpand, toggleIdea, pinnedIdeas, toggleIdeaPin,
  setIdeas, setSupplies, ideaTodos, addIdeaToTodo, removeIdeaFromTodo, reUseIdea,
  schedulingId, setSchedulingId, schedWeekId, setSchedWeekId, weeks, commitIdea, delIdea,
  ideaStages, advanceIdeaStage, IDEA_STAGE_CFG,
  C, ColHeader, InlineEdit, CancelBtn, formatWeekLabel}) {
  const cat        = cats.find(c=>c.label===idea.cat)||{color:C.muted,label:""};
  const isScheduling = schedulingId===idea.id;
  const inTodo     = ideaTodos.includes(idea.id);
  const isExpanded = !!expandedIdeas[idea.id];
  const isPinned   = !!pinnedIdeas[idea.id];
  const stageKey   = ideaStages?.[idea.id]||'concept';
  const stageCfg   = IDEA_STAGE_CFG?.[stageKey]||{offset:87.96,stroke:'#b8bcc8',label:'Concept',hint:'Tap to advance → In progress',check:false};
  const C_CIRC     = 87.96;

  return(
    <div style={{background:idea.done?`${C.highlightSoft}88`:C.card,borderRadius:14,marginBottom:8,overflow:"hidden",boxShadow:"0 1px 8px rgba(0,0,0,0.05)",borderTop:`1.5px solid ${isExpanded?C.accent+"33":idea.done?C.border:"transparent"}`,borderRight:`1.5px solid ${isExpanded?C.accent+"33":idea.done?C.border:"transparent"}`,borderBottom:`1.5px solid ${isExpanded?C.accent+"33":idea.done?C.border:"transparent"}`,borderLeft:isPinned?`3px solid ${C.accent}`:`1.5px solid ${isExpanded?C.accent+"33":idea.done?C.border:"transparent"}`,transition:"all 0.2s",animation:"fadeIn 0.2s ease"}}>

      {/* Collapsed row */}
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 12px",cursor:"pointer"}} onClick={()=>toggleIdeaExpand(idea.id)}>
        <button onClick={e=>{e.stopPropagation();toggleIdea(idea.id);}} style={{width:24,height:24,borderRadius:"50%",flexShrink:0,border:`2px solid ${idea.done?C.accent:C.border}`,background:idea.done?C.accent:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.2s"}}>
          {idea.done&&<span style={{color:"#fff",fontSize:11,lineHeight:1}}>✓</span>}
        </button>
        <InlineEdit value={idea.text||""} placeholder="Untitled"
          onChange={v=>setIdeas(p=>p.map(i=>i.id===idea.id?{...i,text:v}:i))}
          style={{flex:1,fontSize:15,fontFamily:"'Georgia',serif",color:idea.done?C.faint:C.dark,textDecoration:idea.done?"line-through":"none",textDecorationColor:C.accent,lineHeight:1.4,display:"block"}}/>
        {idea.cat&&<span style={{background:cat.color+"20",color:cat.color,fontSize:9,fontFamily:"sans-serif",letterSpacing:1,textTransform:"uppercase",padding:"2px 7px",borderRadius:10,flexShrink:0,opacity:idea.done?0.5:1}}>{idea.cat}</span>}
        {/* Donut — display only on collapsed, no tap handler */}
        <div style={{position:"relative",width:22,height:22,flexShrink:0}}>
          <svg width="22" height="22" viewBox="0 0 36 36" style={{transform:"rotate(-90deg)"}}>
            <circle cx="18" cy="18" r="14" fill="none" stroke="#eaecf1" strokeWidth="3.5"/>
            <circle cx="18" cy="18" r="14" fill="none" stroke={idea.done?C.faint:stageCfg.stroke} strokeWidth="3.5"
              strokeLinecap="round" strokeDasharray={C_CIRC} strokeDashoffset={stageCfg.offset}
              style={{transition:"stroke-dashoffset 0.4s ease,stroke 0.4s ease"}}/>
          </svg>
          {stageCfg.check&&!idea.done&&<span style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",fontSize:8,color:stageCfg.stroke,lineHeight:1}}>✓</span>}
        </div>
        {/* Expand stack — chevron + dots */}
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,flexShrink:0}}>
          <span style={{fontSize:10,color:isExpanded?C.accent:C.faint,transition:"transform 0.2s",display:"inline-block",transform:isExpanded?"rotate(90deg)":"rotate(0deg)",lineHeight:1}}>▶</span>
          {(idea.prep||idea.supplies)&&!isExpanded&&<span style={{fontSize:8,color:C.faint,letterSpacing:1,lineHeight:1}}>•••</span>}
        </div>
        <button onClick={e=>{e.stopPropagation();delIdea(idea.id);}} style={{background:"none",border:"none",color:C.faint,fontSize:16,cursor:"pointer",padding:"0 0 0 2px",lineHeight:1,flexShrink:0}}>×</button>
      </div>

      {/* Expanded detail */}
      {isExpanded&&(
        <div style={{padding:"0 12px 14px",borderTop:`1px solid ${C.border}`}}>

          {/* Stage row — tap to advance, inside expanded only */}
          <div onClick={()=>advanceIdeaStage(idea.id)} style={{display:"flex",alignItems:"center",gap:10,marginTop:10,padding:"8px 10px",borderRadius:10,cursor:"pointer",border:`1.5px solid ${C.border}`,transition:"background 0.15s"}}
            onMouseOver={e=>e.currentTarget.style.background="#f8f8fa"} onMouseOut={e=>e.currentTarget.style.background="transparent"}>
            <div style={{position:"relative",width:26,height:26,flexShrink:0}}>
              <svg width="26" height="26" viewBox="0 0 36 36" style={{transform:"rotate(-90deg)"}}>
                <circle cx="18" cy="18" r="14" fill="none" stroke="#eaecf1" strokeWidth="3.5"/>
                <circle cx="18" cy="18" r="14" fill="none" stroke={idea.done?C.faint:stageCfg.stroke} strokeWidth="3.5"
                  strokeLinecap="round" strokeDasharray={C_CIRC} strokeDashoffset={stageCfg.offset}
                  style={{transition:"stroke-dashoffset 0.4s ease,stroke 0.4s ease"}}/>
              </svg>
              {stageCfg.check&&!idea.done&&<span style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",fontSize:9,color:stageCfg.stroke,lineHeight:1}}>✓</span>}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:2}}>
              <span style={{fontSize:12,fontFamily:"sans-serif",fontWeight:"500",color:idea.done?C.faint:stageCfg.stroke}}>{stageCfg.label}</span>
              <span style={{fontSize:10,fontFamily:"sans-serif",color:C.faint}}>{idea.done?"Mark idea done to reset":stageCfg.hint}</span>
            </div>
            <span style={{marginLeft:"auto",fontSize:10,fontFamily:"sans-serif",color:C.faint}}>→</span>
          </div>

          {/* Status badges */}
          {(idea.scheduledWeekLabel||inTodo)&&(
            <div style={{display:"flex",gap:6,flexWrap:"wrap",paddingTop:10,marginBottom:4}}>
              {idea.scheduledWeekLabel&&<span style={{background:`${C.accent}18`,color:C.accent,fontSize:10,fontFamily:"sans-serif",padding:"2px 8px",borderRadius:10}}>📅 {idea.scheduledWeekLabel}</span>}
              {inTodo&&!idea.done&&<span style={{background:`${C.todo}18`,color:C.todo,fontSize:10,fontFamily:"sans-serif",padding:"2px 8px",borderRadius:10}}>✅ In to-do</span>}
            </div>
          )}

          {/* Category reassign */}
          <div style={{marginTop:10}}>
            <ColHeader style={{marginBottom:6}}>Category</ColHeader>
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
              <button onClick={()=>setIdeas(p=>p.map(i=>i.id===idea.id?{...i,cat:""}:i))}
                style={{background:!idea.cat?"rgba(0,0,0,0.08)":"transparent",color:!idea.cat?C.dark:C.faint,border:`1.5px solid ${!idea.cat?C.muted:C.border}`,borderRadius:20,padding:"3px 10px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>None</button>
              {cats.map(c=>(
                <button key={c.id} onClick={()=>setIdeas(p=>p.map(i=>i.id===idea.id?{...i,cat:c.label}:i))}
                  style={{background:idea.cat===c.label?c.color:"transparent",color:idea.cat===c.label?"#fff":c.color,border:`1.5px solid ${c.color}`,borderRadius:20,padding:"3px 10px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>{c.label}</button>
              ))}
            </div>
          </div>

          {/* Prep notes */}
          <div style={{marginTop:10}}>
            <ColHeader style={{marginBottom:4}}>Prep notes</ColHeader>
            <InlineEdit value={idea.prep||""} multiline placeholder="Tap to add prep notes..."
              onChange={v=>setIdeas(p=>p.map(i=>i.id===idea.id?{...i,prep:v}:i))}
              style={{fontSize:13,lineHeight:1.6,color:idea.prep?C.mid:C.faint,fontFamily:"'Georgia',serif",display:"block",whiteSpace:"pre-wrap",fontStyle:idea.prep?"normal":"italic"}}/>
          </div>

          {/* Supplies */}
          <div style={{marginTop:10}}>
            <ColHeader style={{marginBottom:4,color:"#a89bb5"}}>Supplies needed</ColHeader>
            <InlineEdit value={idea.supplies||""} placeholder="Tap to add supplies..."
              onChange={v=>{
                setIdeas(p=>p.map(i=>i.id===idea.id?{...i,supplies:v}:i));
                if(v.trim()){
                  const items=v.split(",").map(s=>s.trim()).filter(Boolean);
                  setSupplies(p=>{
                    const existing=p.filter(s=>s.sourceIdea===idea.text);
                    const others=p.filter(s=>s.sourceIdea!==idea.text);
                    return [...others,...items.map(t=>({id:"su-"+Date.now()+Math.random(),text:t,purchased:false,sourceIdea:idea.text}))];
                  });
                } else {
                  setSupplies(p=>p.filter(s=>s.sourceIdea!==idea.text));
                }
              }}
              style={{fontSize:13,lineHeight:1.6,color:idea.supplies?C.mid:C.faint,fontFamily:"'Georgia',serif",display:"block",fontStyle:idea.supplies?"normal":"italic"}}/>
          </div>

          {/* Action row */}
          <div style={{marginTop:12,display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            {!idea.done&&!isScheduling&&<button onClick={()=>{setSchedulingId(idea.id);setSchedWeekId(weeks[weeks.length-1]?.id||"");}} style={{background:"transparent",border:`1.5px solid ${C.accent}`,borderRadius:8,padding:"5px 12px",fontSize:12,fontFamily:"sans-serif",color:C.accent,cursor:"pointer"}}>→ Schedule</button>}
            {!idea.done&&!inTodo&&<button onClick={()=>addIdeaToTodo(idea.id)} style={{background:"transparent",border:`1.5px solid ${C.todo}`,borderRadius:8,padding:"5px 12px",fontSize:12,fontFamily:"sans-serif",color:C.todo,cursor:"pointer"}}>+ To-do</button>}
            {!idea.done&&inTodo&&<button onClick={()=>removeIdeaFromTodo(idea.id)} style={{background:"transparent",border:`1.5px solid ${C.faint}`,borderRadius:8,padding:"5px 12px",fontSize:12,fontFamily:"sans-serif",color:C.faint,cursor:"pointer"}}>Remove from to-do</button>}
            {idea.done&&<button onClick={()=>reUseIdea(idea.id)} style={{background:"transparent",border:`1.5px solid ${C.muted}`,borderRadius:8,padding:"5px 12px",fontSize:12,fontFamily:"sans-serif",color:C.muted,cursor:"pointer"}}>↩ Reuse</button>}
            <button onClick={()=>toggleIdeaPin(idea.id)} style={{marginLeft:"auto",background:isPinned?`${C.accent}15`:"none",border:"none",borderRadius:8,padding:"4px 8px",fontSize:12,fontFamily:"sans-serif",color:isPinned?C.accent:C.faint,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
              📌 {isPinned?"Pinned":"Pin"}
            </button>
          </div>

          {/* Schedule picker */}
          {isScheduling&&(
            <div style={{marginTop:12,padding:"12px 14px",background:C.accentSoft,borderRadius:10,border:`1px solid ${C.border}`}}>
              <ColHeader style={{marginBottom:8}}>Schedule to which week?</ColHeader>
              <select value={schedWeekId} onChange={e=>setSchedWeekId(e.target.value)}
                style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:14,fontFamily:"sans-serif",color:C.dark,background:"white",marginBottom:10,outline:"none"}}>
                {weeks.map(w=><option key={w.id} value={w.id}>Week of {formatWeekLabel(w.sunday)}</option>)}
              </select>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <CancelBtn onClick={()=>setSchedulingId(null)}/>
                <button onClick={()=>commitIdea(idea)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"7px 16px",fontSize:13,fontFamily:"sans-serif",cursor:"pointer"}}>Confirm</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [tab,setTab] = useState(()=>{ try{ return localStorage.getItem("cc_tab")||"board"; }catch(e){ return "board"; } });
  const switchTab = (t) => { setTab(t); try{ localStorage.setItem("cc_tab",t); }catch(e){} };
  const bottomRef               = useRef(null);
  const currentWeekRef          = useRef(null);
  const draftRef                = useRef({});
  const today                   = new Date();
  const [loaded,setLoaded]      = useState(false);
  const [user,setUser]          = useState(null);
  const [authChecked,setAuthChecked] = useState(false);
  const [authMode,setAuthMode]  = useState("login"); // "login" | "signup"
  const [authEmail,setAuthEmail] = useState("");
  const [authPassword,setAuthPassword] = useState("");
  const [authError,setAuthError] = useState("");
  const [authLoading,setAuthLoading] = useState(false);
  const ADMIN_EMAIL = "kerrcarrie5@gmail.com";
  const [showPassword,setShowPassword] = useState(false);
  const [forgotSent,setForgotSent]     = useState(false);
  const [isRecovery,setIsRecovery]     = useState(typeof window !== "undefined" && window.location.search.includes("reset=true"));
  const [recoveryPassword,setRecoveryPassword]   = useState("");
  const [recoveryPassword2,setRecoveryPassword2] = useState("");
  const [recoveryMessage,setRecoveryMessage]     = useState("");
  const [showSettings,setShowSettings] = useState(false);
  const [showSettingsHelp,setShowSettingsHelp] = useState(false);
  const [showCalHelp,setShowCalHelp] = useState(false);
  const [showNewHereBanner,setShowNewHereBanner] = useState(false);
  const [hideEmptyWeeks,setHideEmptyWeeks] = useState(false);
  const [todoViewMode,setTodoViewMode] = useState("type");
  const [archiveWeeks,setArchiveWeeks] = useState(2);

  // Settings
  const [userName,setUserName]  = useState("Carrie ASMR");
  const [cats,setCats]          = useState(DEFAULT_IDEA_CATS);
  const [evtTags,setEvtTags]    = useState(DEFAULT_EVENT_TAGS);
  const [newCatLabel,setNewCatLabel] = useState("");
  const [newCatColor,setNewCatColor] = useState("");
  const [newEvtTagLabel,setNewEvtTagLabel] = useState("");
  const [newEvtTagColor,setNewEvtTagColor] = useState("");
  const [editingCatId,setEditingCatId]     = useState(null);
  const [editingEvtTagId,setEditingEvtTagId] = useState(null);

  // Calendar nav
  const [calMonth,setCalMonth]  = useState(today.getMonth());
  const [calYear,setCalYear]    = useState(today.getFullYear());
  const [selDate,setSelDate]    = useState(null);
  const [showArchived,setShowArchived] = useState(false);

  // Holidays

  const [activeCountries,setActiveCountries]  = useState([]);
  const [showHolidayPicker,setShowHolidayPicker] = useState(false);

  // ── Holiday engine — no API, calculated locally ──
  const THIS_YEAR = new Date().getFullYear();
  const ALL_HOLIDAYS = getHolidaysForYear(THIS_YEAR);

  const toggleCountry      = (code)=>setActiveCountries(p=>p.includes(code)?p.filter(c=>c!==code):[...p,code]);

  const getHolidaysForDate = (date) => {
    if(!activeCountries.length) return [];
    const raw = ALL_HOLIDAYS.filter(h=>
      h.date && isSameDay(h.date, date) &&
      h.countries.some(c=>activeCountries.includes(c))
    );
    // Group by name
    const grouped = {};
    raw.forEach(h=>{
      const key = h.name;
      if(!grouped[key]){
        grouped[key] = {...h, matchedCountries: h.countries.filter(c=>activeCountries.includes(c))};
      } else {
        h.countries.filter(c=>activeCountries.includes(c)).forEach(c=>{
          if(!grouped[key].matchedCountries.includes(c)) grouped[key].matchedCountries.push(c);
        });
      }
    });
    return Object.values(grouped);
  };

  // Weeks
  const [weeks,setWeeks] = useState(()=>{
    const s=getSundayOfWeek(new Date());
    return [{id:"w-"+Date.now(),sunday:s,entries:[],draftVideoName:"",draftPrep:"",isWriting:false,note:"",tint:"lavender",collapsed:true}];
  });
  const WEEK_TINTS = ['lavender','sage','sand','steel'];
  const addWeek=()=>{
    setWeeks(p=>{
      const tint = WEEK_TINTS[p.length % 4];
      return [...p,{id:"w-"+Date.now(),sunday:getNextSunday(p[p.length-1].sunday),entries:[],draftVideoName:"",draftPrep:"",isWriting:false,note:"",collapsed:true,tint}];
    });
    setTimeout(()=>bottomRef.current?.scrollIntoView({behavior:"smooth"}),100);
  };
  const toggleCollapse=(id)=>setWeeks(p=>p.map(w=>w.id===id?{...w,collapsed:!w.collapsed}:w));
  const archiveThreshold = () => { const d=new Date(); d.setDate(d.getDate()-(archiveWeeks*7)); d.setHours(0,0,0,0); return d; };
  const visibleWeeks  = weeks.filter(w=>new Date(w.sunday)>=archiveThreshold());
  const archivedWeeks = weeks.filter(w=>new Date(w.sunday)<archiveThreshold());

  // Calendar events
  const [calEvts,setCalEvts]             = useState([]);
  const [addingEvt,setAddingEvt]         = useState(false);
  const [evtLabel,setEvtLabel]           = useState("");
  const [evtDetail,setEvtDetail]         = useState("");
  const [evtSpan,setEvtSpan]             = useState("day");
  const [evtRangeStart,setEvtRangeStart] = useState(null);
  const [evtRangeEnd,setEvtRangeEnd]     = useState(null);
  const [rangePickStep,setRangePickStep] = useState(0);

  // Ideas / supplies / todos / notes
  const [ideas,setIdeas]             = useState([]);
  const [ideaText,setIdeaText]       = useState("");
  const [ideaPrep,setIdeaPrep]       = useState("");
  const [ideaSupply,setIdeaSupply]   = useState("");
  const [ideaCat,setIdeaCat]         = useState("");
  const [ideaFilter,setIdeaFilter]   = useState("all");
  const ideaRef                      = useRef(null);
  const [supplies,setSupplies]       = useState([]);
  const [supplyText,setSupplyText]   = useState("");
  const supplyRef                    = useRef(null);
  const [manualTodos,setManualTodos] = useState([]);
  const [newTodoText,setNewTodoText] = useState("");
  const [ideaTodos,setIdeaTodos]     = useState([]);
  const [todoFilter,setTodoFilter]   = useState("active");
  const [notes,setNotes]             = useState("");
  const [schedulingId,setSchedulingId] = useState(null);
  const [showNewIdeaForm,setShowNewIdeaForm] = useState(false);
  const [ideaStages,setIdeaStages] = useState({});
  const IDEA_STAGES = ['concept','progress','ready'];
  const IDEA_STAGE_CFG = {
    concept:  {offset:87.96, stroke:'#b8bcc8', label:'Concept',     hint:'Tap to advance → In progress', check:false},
    progress: {offset:43.98, stroke:'#b58a9b', label:'In progress', hint:'Tap to advance → Ready',       check:false},
    ready:    {offset:0,     stroke:'#7aaa8a', label:'Ready',       hint:'Tap to cycle back → Concept',  check:true},
  };
  const advanceIdeaStage = (id) => {
    setIdeaStages(p=>{
      const cur = p[id]||'concept';
      const next = IDEA_STAGES[(IDEA_STAGES.indexOf(cur)+1)%IDEA_STAGES.length];
      return {...p,[id]:next};
    });
  };
  const [expandedIdeas,setExpandedIdeas] = useState({});
  const [pinnedIdeas,setPinnedIdeas]     = useState({});
  const [ideaCatFilter,setIdeaCatFilter]   = useState("all");
  const [ideaStageFilter,setIdeaStageFilter] = useState("all");
  const toggleIdeaPin = (id) => setPinnedIdeas(p=>({...p,[id]:!p[id]}));
  const toggleIdeaExpand = (id) => setExpandedIdeas(p=>({...p,[id]:!p[id]}));
  const [schedWeekId,setSchedWeekId]   = useState("");

  // ── Storage ────────────────────────────────────────
  const saveTimers = useRef({});
  const userRef = useRef(null);
  useEffect(()=>{ userRef.current=user; },[user]);

  const save = useRef((key,val)=>{
    clearTimeout(saveTimers.current[key]);
    saveTimers.current[key]=setTimeout(async()=>{
      const uid = userRef.current?.id;
      if(!uid) return;
      try {
        const update = { [key]: val, updated_at: new Date().toISOString() };
        const { data } = await supabase.from("cvp_data").select("id").eq("user_id",uid).single();
        if(data) {
          await supabase.from("cvp_data").update(update).eq("user_id",uid);
        } else {
          await supabase.from("cvp_data").insert({user_id:uid,...update});
        }
      } catch(e){ console.log("Save error:",e); }
    },800);
  });

  // ── Auth check on mount ──
  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{
      setUser(session?.user||null);
      setAuthChecked(true);
    });
    const {data:{subscription}} = supabase.auth.onAuthStateChange((_,session)=>{
      setUser(session?.user||null);
      if(session?.user){
        setTab("board");
        setShowSettings(false);
      }
    });
    return ()=>subscription.unsubscribe();
  },[]);

  // ── Load data when user is known ──
  useEffect(()=>{
    if(!authChecked) return;
    if(!user) { setLoaded(true); return; }
    const load=async()=>{
      try{
        const revW=(raw)=>{
          const today2=new Date();
          return raw.map((w,wi)=>{
            const sun=new Date(w.sunday);
            const isCurrent=sun<=today2&&today2<new Date(sun.getTime()+7*86400000);
            return {...w,sunday:sun,isWriting:false,draftVideoName:"",draftPrep:"",note:w.note||"",collapsed:w.collapsed!==undefined?w.collapsed:true,tint:w.tint||['lavender','sage','sand','steel'][wi%4],entries:(w.entries||[]).map(e=>({...e,day:e.day||null,prepDone:!!e.prepDone}))};
          });
        };
        const revE=(raw)=>raw.map(e=>({...e,date:new Date(e.date),sunday:e.sunday?new Date(e.sunday):getSundayOfWeek(new Date(e.date)),endDate:e.endDate?new Date(e.endDate):null}));
        const { data, error } = await supabase
          .from("cvp_data")
          .select("*")
          .eq("user_id", user.id)
          .single();
        if(error && error.code !== "PGRST116") throw error;
        if(data) {
          if(data.weeks?.length)      setWeeks(revW(data.weeks));
          if(data.events?.length)     setCalEvts(revE(data.events));
          if(data.ideas?.length)      setIdeas(data.ideas);
          if(data.supplies?.length)   setSupplies(data.supplies);
          if(data.todos?.length)      setManualTodos(data.todos);
          if(data.ideatodos?.length)  setIdeaTodos(data.ideatodos);
          if(data.pinnedideas)        setPinnedIdeas(data.pinnedideas||{});
          if(data.ideastages)         setIdeaStages(data.ideastages||{});
          if(data.notes)              setNotes(data.notes);

          if(data.countries?.length)  setActiveCountries(data.countries);
          if(data.cats?.length)       setCats(data.cats);
          if(data.evttags?.length)    setEvtTags(data.evttags);
          if(data.settings?.userName) setUserName(data.settings.userName);
          if(data.settings?.archiveWeeks!=null) setArchiveWeeks(data.settings.archiveWeeks);
          // Show new here banner only if truly new (no weeks yet)
          if(!data.weeks?.length) setShowNewHereBanner(true);
        } else {
          // No data row at all — brand new user
          setShowNewHereBanner(true);
        }
      }catch(e){console.log("Load error:",e);}
      setLoaded(true);
      // Scroll to current week after load
      setTimeout(()=>currentWeekRef.current?.scrollIntoView({behavior:"smooth",block:"start"}),300);
    };
    load();
  },[user,authChecked]);

  // Stable refs for save effects
  const wRef=useRef(weeks);  useEffect(()=>{wRef.current=weeks;},[weeks]);
  const eRef=useRef(calEvts);useEffect(()=>{eRef.current=calEvts;},[calEvts]);

  useEffect(()=>{if(!loaded)return;save.current("weeks",weeks.map(w=>({...w,sunday:w.sunday instanceof Date?w.sunday.toISOString():w.sunday,isWriting:false,draftVideoName:"",draftPrep:""})));},[weeks,loaded]);
  useEffect(()=>{if(!loaded)return;save.current("events",calEvts.map(e=>({...e,date:e.date instanceof Date?e.date.toISOString():e.date,sunday:e.sunday instanceof Date?e.sunday.toISOString():e.sunday,endDate:e.endDate instanceof Date?e.endDate.toISOString():e.endDate})));},[calEvts,loaded]);
  useEffect(()=>{if(loaded)save.current("ideas",ideas);},[ideas,loaded]);
  useEffect(()=>{if(loaded)save.current("ideastages",ideaStages);},[ideaStages,loaded]);
  useEffect(()=>{if(loaded)save.current("pinnedideas",pinnedIdeas);},[pinnedIdeas,loaded]);
  useEffect(()=>{if(loaded)save.current("supplies",supplies);},[supplies,loaded]);
  useEffect(()=>{if(loaded)save.current("todos",manualTodos);},[manualTodos,loaded]);
  useEffect(()=>{if(loaded)save.current("ideatodos",ideaTodos);},[ideaTodos,loaded]);
  useEffect(()=>{if(loaded)save.current("notes",notes);},[notes,loaded]);
  useEffect(()=>{if(loaded)save.current("countries",activeCountries);},[activeCountries,loaded]);
  useEffect(()=>{if(loaded)save.current("cats",cats);},[cats,loaded]);
  useEffect(()=>{if(loaded)save.current("evttags",evtTags);},[evtTags,loaded]);
  useEffect(()=>{if(loaded)save.current("settings",{userName, archiveWeeks, signup_country: user?.user_metadata?.signup_country||""});},[userName,archiveWeeks,loaded,user]);

  // ── Week helpers ───────────────────────────────────
  const updWeek       = (id,ch)=>setWeeks(p=>p.map(w=>w.id===id?{...w,...ch}:w));
  const startWriting  = (id)=>{ updWeek(id,{isWriting:true}); setTimeout(()=>draftRef.current[id]?.focus(),100); };
  const DAYS_ORDER = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const sortEntries = (entries) => {
    const unassigned = entries.filter(e=>!e.day);
    const assigned   = entries.filter(e=>e.day).sort((a,b)=>DAYS_ORDER.indexOf(a.day)-DAYS_ORDER.indexOf(b.day));
    return [...unassigned,...assigned];
  };
  const saveEntry     = (id)=>setWeeks(p=>p.map(w=>{
    if(w.id!==id||(!w.draftVideoName.trim()&&!w.draftPrep.trim()))return w;
    const newEntries = [...w.entries,{id:"e-"+Date.now(),videoName:w.draftVideoName,prep:w.draftPrep,day:null,prepDone:false,stage:0}];
    return{...w,entries:sortEntries(newEntries),draftVideoName:"",draftPrep:"",isWriting:false};
  }));
  const updEntry      = (wid,eid,ch)=>setWeeks(p=>p.map(w=>w.id===wid?{...w,entries:w.entries.map(e=>e.id===eid?{...e,...ch}:e)}:w));
  const advanceStage  = (wid,eid)=>{
    const entry = weeks.find(w=>w.id===wid)?.entries.find(e=>e.id===eid);
    if(entry) updEntry(wid,eid,{stage:((entry.stage||0)+1)%5});
  };
  const TINT_CFG = {
    lavender:{ header:'#c8ccde',label:'#5a5e80',date:'#1e2035',note:'#6068a0',chip:'rgba(80,80,140,0.3)',calActive:'#7078a8',calDim:'rgba(255,255,255,0.82)',calOff:'rgba(255,255,255,0.45)',pillBg:'rgba(255,255,255,0.45)',pillText:'#3a4068' },
    sage:    { header:'#b8d4c4',label:'#306848',date:'#102818',note:'#308050',chip:'rgba(40,120,70,0.3)', calActive:'#3a7858',calDim:'rgba(255,255,255,0.82)',calOff:'rgba(255,255,255,0.45)',pillBg:'rgba(255,255,255,0.45)',pillText:'#103820' },
    sand:    { header:'#d4c4b0',label:'#705030',date:'#281808',note:'#806040',chip:'rgba(120,80,40,0.3)',calActive:'#805838',calDim:'rgba(255,255,255,0.82)',calOff:'rgba(255,255,255,0.45)',pillBg:'rgba(255,255,255,0.45)',pillText:'#301800' },
    steel:   { header:'#b8ccd8',label:'#305868',date:'#101e28',note:'#407888',chip:'rgba(40,90,120,0.3)',calActive:'#306880',calDim:'rgba(255,255,255,0.82)',calOff:'rgba(255,255,255,0.45)',pillBg:'rgba(255,255,255,0.45)',pillText:'#101e28' },
    past:    { header:'#d8d8dc',label:'#8888a0',date:'#404048',note:'#909098',chip:'rgba(0,0,0,0.1)',   calActive:'#8888a0',calDim:'rgba(255,255,255,0.82)',calOff:'rgba(255,255,255,0.45)',pillBg:'rgba(255,255,255,0.45)',pillText:'#606068' },
  };

  // Build a 5-row mini calendar grid — uses whichever month has more days in the week
  const buildMiniCal = (sunday, tintKey) => {
    const t = TINT_CFG[tintKey] || TINT_CFG.lavender;
    const weekEnd = new Date(sunday.getTime() + 6*86400000);
    // Count days in each month this week spans
    const sunMonth = sunday.getMonth(), sunYear = sunday.getFullYear();
    const endMonth = weekEnd.getMonth(), endYear = weekEnd.getFullYear();
    let month, year;
    if(sunMonth === endMonth) {
      month = sunMonth; year = sunYear;
    } else {
      // Count how many days fall in each month
      const daysInSunMonth = weekEnd.getDate() === 0 ? 7 : 7 - weekEnd.getDate();
      const daysInEndMonth = weekEnd.getDate();
      if(daysInEndMonth > daysInSunMonth) { month = endMonth; year = endYear; }
      else { month = sunMonth; year = sunYear; }
    }
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month+1, 0).getDate();
    const weekStart = sunday;
    const weekFinish = new Date(sunday.getTime()+7*86400000);
    const rows = [];
    let dayNum = 1 - firstDay;
    for(let r=0;r<5;r++){
      const row = [];
      for(let c=0;c<7;c++){
        const d = dayNum;
        const inMonth = d>=1 && d<=daysInMonth;
        const dayDate = inMonth ? new Date(year,month,d) : null;
        const inActiveWeek = dayDate && dayDate >= weekStart && dayDate < weekFinish;
        const isEmpty = !inMonth && d < 1;
        const isOverflow = !inMonth && d > daysInMonth;
        let bg;
        if(isEmpty) bg = 'transparent';
        else if(inActiveWeek) bg = t.calActive;
        else if(isOverflow) bg = t.calOff;
        else bg = t.calDim;
        row.push(bg);
        dayNum++;
      }
      rows.push(row);
    }
    return rows.map((row,ri)=>(
      <div key={ri} style={{display:'flex',gap:2}}>
        {row.map((bg,ci)=>(
          <div key={ci} style={{width:8,height:8,borderRadius:2,background:bg}}/>
        ))}
      </div>
    ));
  };

  const STAGES = [
    {label:"Created", hint:"Tap when ready to film →", color:C.faint,   offset:87.96 },
    {label:"Created", hint:"Tap when filmed →",         color:C.accent,  offset:65.97 },
    {label:"Filmed",  hint:"Tap when edited →",         color:C.event,   offset:43.98 },
    {label:"Edited",  hint:"Tap when posted →",         color:"#bfaa8a", offset:21.99 },
    {label:"Posted",  hint:"Tap to reset",              color:C.faint,   offset:0     },
  ];
  const assignDay     = (wid,eid,d)=>setWeeks(p=>p.map(w=>w.id===wid?{...w,entries:sortEntries(w.entries.map(e=>e.id===eid?{...e,day:e.day===d?null:d}:e))}:w));
  const cancelDraft   = (id)=>updWeek(id,{draftVideoName:"",draftPrep:"",isWriting:false});
  const delEntry      = (wid,eid)=>setWeeks(p=>p.map(w=>w.id===wid?{...w,entries:w.entries.filter(e=>e.id!==eid)}:w));
  const getEntries    = (date)=>{ const r=[]; for(const w of weeks)if(isInWeek(w.sunday,date))w.entries.forEach(e=>r.push({...e,weekId:w.id})); return r; };
  const togglePrepDone= (wid,eid)=>{ const e=weeks.find(w=>w.id===wid)?.entries.find(e=>e.id===eid); if(e)updEntry(wid,eid,{prepDone:!e.prepDone}); };

  // ── Calendar event helpers ─────────────────────────
  const saveEvt = ()=>{
    if(!evtLabel.trim())return;
    if(evtSpan==="range"&&evtRangeStart&&evtRangeEnd){
      setCalEvts(p=>[...p,{id:"ev-"+Date.now(),label:evtLabel.trim(),detail:evtDetail.trim(),span:"range",date:new Date(evtRangeStart),endDate:new Date(evtRangeEnd),sunday:getSundayOfWeek(evtRangeStart)}]);
    } else if(evtSpan==="day"&&selDate){
      setCalEvts(p=>[...p,{id:"ev-"+Date.now(),label:evtLabel.trim(),detail:evtDetail.trim(),span:"day",date:new Date(selDate),sunday:getSundayOfWeek(selDate),endDate:null}]);
    }
    setEvtLabel(""); setEvtDetail(""); setEvtSpan("day"); setAddingEvt(false);
    setEvtRangeStart(null); setEvtRangeEnd(null); setRangePickStep(0);
  };
  const delEvt = (id)=>setCalEvts(p=>p.filter(e=>e.id!==id));
  const getEvtsForDate = (date)=>calEvts.filter(e=>{
    if(e.span==="day") return isSameDay(e.date,date);
    if(e.span==="range") return e.endDate?isInRange(e.date,e.endDate,date):isSameDay(e.date,date);
    return false;
  });
  const getWeekEvts = (sun)=>calEvts.filter(e=>{
    if(e.span==="day") return isInWeek(sun,e.date);
    if(e.span==="range") return e.endDate?(isInRange(e.date,e.endDate,sun)||isInRange(e.date,e.endDate,new Date(sun.getTime()+6*86400000))):false;
    return false;
  });

  const grid      = buildGrid(calYear,calMonth);
  const prevMonth = ()=>{ if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1); setSelDate(null); };
  const nextMonth = ()=>{ if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1); setSelDate(null); };
  const selEntries  = selDate?getEntries(selDate):[];
  const selWeek     = selDate?(weeks.find(w=>isInWeek(w.sunday,selDate))||null):null;
  const selEvts     = selDate?getEvtsForDate(selDate):[];
  const selHolidays = selDate?getHolidaysForDate(selDate):[];

  const handleDateTap = (date)=>{
    if(rangePickStep===1){
      setEvtRangeStart(new Date(date));
      setEvtRangeEnd(null);
      setRangePickStep(2);
      return;
    }
    if(rangePickStep===2){
      const start=evtRangeStart;
      if(date<start){ setEvtRangeEnd(new Date(start)); setEvtRangeStart(new Date(date)); }
      else setEvtRangeEnd(new Date(date));
      setRangePickStep(0);
      return;
    }
    if(selDate&&isSameDay(date,selDate)) setSelDate(null);
    else setSelDate(new Date(date));
    setAddingEvt(false);
  };

  // ── Idea helpers ───────────────────────────────────
  const addIdea = ()=>{
    if(!ideaText.trim())return;
    const text   = ideaText.trim();
    const prep   = ideaPrep.trim();
    const supply = ideaSupply.trim();
    const cat    = ideaCat||"";
    if(supply){
      const items = supply.split(",").map(s=>s.trim()).filter(Boolean);
      setSupplies(p=>[...p,...items.map(t=>({id:"su-"+Date.now()+Math.random(),text:t,purchased:false,sourceIdea:text}))]);
    }
    setIdeas(p=>[{id:"id-"+Date.now(),text,prep,supplies:supply,cat,done:false},...p]);
    setIdeaText(""); setIdeaPrep(""); setIdeaSupply(""); ideaRef.current?.focus();
  };
  const toggleIdea       = (id)=>setIdeas(p=>p.map(i=>i.id===id?{...i,done:!i.done}:i));
  const delIdea          = (id)=>{ setIdeas(p=>p.filter(i=>i.id!==id)); setIdeaTodos(p=>p.filter(x=>x!==id)); };
  const doneCount        = ideas.filter(i=>i.done).length;
  const filtIdeas        = ideas.filter(i=>{
    const statusOk = ideaFilter==="active"?!i.done:ideaFilter==="done"?i.done:true;
    const catOk    = ideaCatFilter==="all"||(i.cat||"")===(ideaCatFilter);
    const stageOk  = ideaStageFilter==="all"||(ideaStages[i.id]||"concept")===ideaStageFilter;
    return statusOk && catOk && stageOk;
  });
  const pinnedFilt   = filtIdeas.filter(i=>pinnedIdeas[i.id]);
  const unpinnedFilt = filtIdeas.filter(i=>!pinnedIdeas[i.id]);
  const addIdeaToTodo    = (id)=>{ if(!ideaTodos.includes(id))setIdeaTodos(p=>[...p,id]); };
  const removeIdeaFromTodo=(id)=>setIdeaTodos(p=>p.filter(x=>x!==id));
  const commitIdea = (idea)=>{
    if(!schedWeekId)return;
    setWeeks(p=>p.map(w=>w.id!==schedWeekId?w:{...w,entries:[...w.entries,{id:"e-"+Date.now(),videoName:idea.text,prep:idea.prep||"",day:null,prepDone:false}]}));
    const week=weeks.find(w=>w.id===schedWeekId);
    setIdeas(p=>p.map(i=>i.id===idea.id?{...i,done:true,scheduledWeekId:schedWeekId,scheduledWeekLabel:week?formatWeekLabel(week.sunday):""}:i));
    setSchedulingId(null); setSchedWeekId("");
  };
  const reUseIdea=(id)=>setIdeas(p=>p.map(i=>i.id===id?{...i,done:false,scheduledWeekId:null,scheduledWeekLabel:""}:i));

  // ── Supply helpers ─────────────────────────────────
  const addSupply    = ()=>{ if(!supplyText.trim())return; setSupplies(p=>[...p,{id:"su-"+Date.now(),text:supplyText.trim(),purchased:false}]); setSupplyText(""); supplyRef.current?.focus(); };
  const toggleSupply = (id)=>setSupplies(p=>p.map(s=>s.id===id?{...s,purchased:!s.purchased}:s));
  const delSupply    = (id)=>setSupplies(p=>p.filter(s=>s.id!==id));
  const needed       = supplies.filter(s=>!s.purchased);
  const purchased    = supplies.filter(s=>s.purchased);

  // ── To-do helpers ──────────────────────────────────
  const addManualTodo   = ()=>{ if(!newTodoText.trim())return; setManualTodos(p=>[...p,{id:"td-"+Date.now(),text:newTodoText.trim(),done:false}]); setNewTodoText(""); };
  const toggleTodo      = (id)=>setManualTodos(p=>p.map(t=>t.id===id?{...t,done:!t.done}:t));
  const delTodo         = (id)=>setManualTodos(p=>p.filter(t=>t.id!==id));
  const clearDoneTodos  = ()=>setManualTodos(p=>p.filter(t=>!t.done));

  // Scheduled todos: from video entries with prep notes in visible weeks
  const scheduledTodos = [];
  visibleWeeks.forEach(w=>{ w.entries.forEach(e=>{ if(e.prep&&e.prep.trim()) scheduledTodos.push({entryId:e.id,weekId:w.id,videoName:e.videoName,prep:e.prep,weekLabel:formatWeekLabel(w.sunday),weekSunday:w.sunday,done:!!e.prepDone}); }); });

  // Filter todos
  const filtScheduled = todoFilter==="active"?scheduledTodos.filter(t=>!t.done):todoFilter==="done"?scheduledTodos.filter(t=>t.done):scheduledTodos;
  const ideaTodoItems = ideaTodos.map(id=>ideas.find(i=>i.id===id)).filter(Boolean);
  const filtManual    = todoFilter==="active"?manualTodos.filter(t=>!t.done):todoFilter==="done"?manualTodos.filter(t=>t.done):manualTodos;
  const hasDoneManual = manualTodos.some(t=>t.done);
  const totalActive   = scheduledTodos.filter(t=>!t.done).length + ideaTodoItems.filter(i=>!i.done).length + manualTodos.filter(t=>!t.done).length;
  const totalTodos    = scheduledTodos.length + ideaTodoItems.length + manualTodos.length;

  // ── To-do by week ──
  const todosByWeek = () => {
    const weekMap = {};
    filtScheduled.forEach(item=>{
      const key = item.weekLabel;
      if(!weekMap[key]) weekMap[key] = {label:key, sunday:item.weekSunday, items:[]};
      weekMap[key].items.push(item);
    });
    return Object.values(weekMap).sort((a,b)=>new Date(a.sunday)-new Date(b.sunday));
  };

  // ── Category helpers ───────────────────────────────
  const addCat    = ()=>{ if(!newCatLabel.trim())return; const nextColor=TAG_COLORS[cats.length%TAG_COLORS.length]; setCats(p=>[...p,{id:"cat-"+Date.now(),label:newCatLabel.trim(),color:newCatColor||nextColor}]); setNewCatLabel(""); };
  const delCat    = (id)=>setCats(p=>p.filter(c=>c.id!==id));
  const updCat    = (id,ch)=>setCats(p=>p.map(c=>c.id===id?{...c,...ch}:c));
  const addEvtTag = ()=>{ if(!newEvtTagLabel.trim())return; const nextColor=TAG_COLORS[evtTags.length%TAG_COLORS.length]; setEvtTags(p=>[...p,{id:"et-"+Date.now(),label:newEvtTagLabel.trim(),color:newEvtTagColor||nextColor}]); setNewEvtTagLabel(""); };
  const delEvtTag = (id)=>setEvtTags(p=>p.filter(t=>t.id!==id));
  const updEvtTag = (id,ch)=>setEvtTags(p=>p.map(t=>t.id===id?{...t,...ch}:t));

  // ── Auth functions ────────────────────────────────
  const handleSignIn = async()=>{
    setAuthLoading(true); setAuthError("");
    const {error} = await supabase.auth.signInWithPassword({email:authEmail,password:authPassword});
    if(error) setAuthError(error.message);
    setAuthLoading(false);
  };
  const handleSignUp = async()=>{
    setAuthLoading(true); setAuthError("");
    // Silently get country from IP before signup
    let signupCountry = "";
    try {
      const geo = await fetch("https://ipapi.co/json/").then(r=>r.json());
      signupCountry = geo.country_name || "";
    } catch(e){ console.log("Geo lookup failed:",e); }
    const {data:signupData, error} = await supabase.auth.signUp({
      email:authEmail,
      password:authPassword,
      options:{ data:{ signup_country: signupCountry } }
    });
    if(error) setAuthError(error.message);
    else setAuthError("Check your email to confirm your account, then log in.");
    setAuthLoading(false);
  };
  const handleSignOut = async()=>{ await supabase.auth.signOut(); setLoaded(false); };
  const handleUpdatePassword = async()=>{
    setAuthLoading(true); setAuthError(""); setRecoveryMessage("");
    if(!recoveryPassword.trim()||!recoveryPassword2.trim()){setAuthError("Enter your new password twice.");setAuthLoading(false);return;}
    if(recoveryPassword!==recoveryPassword2){setAuthError("Passwords do not match.");setAuthLoading(false);return;}
    const {error} = await supabase.auth.updateUser({password:recoveryPassword});
    if(error){setAuthError(error.message);}
    else{
      setRecoveryMessage("Password updated. Please sign in with your new password.");
      setRecoveryPassword(""); setRecoveryPassword2("");
      await supabase.auth.signOut();
      window.history.replaceState({},document.title,window.location.pathname);
      setIsRecovery(false);
    }
    setAuthLoading(false);
  };
  const handleForgotPassword = async()=>{
    if(!authEmail.trim()){ setAuthError("Enter your email above first"); return; }
    setAuthLoading(true);
    const {error} = await supabase.auth.resetPasswordForEmail(authEmail, {
      redirectTo: window.location.origin,
    });
    if(error) setAuthError(error.message);
    else setForgotSent(true);
    setAuthLoading(false);
  };

  // ── Auth screen ──
  if(!authChecked||!user) return(
    <div style={{minHeight:"100vh",background:C.page,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{background:C.card,borderRadius:20,padding:32,width:"100%",maxWidth:360,boxShadow:"0 4px 24px rgba(0,0,0,0.1)"}}>
        <p style={{color:C.muted,fontSize:11,letterSpacing:3,textTransform:"uppercase",margin:"0 0 6px",fontFamily:"sans-serif",textAlign:"center"}}>ContentCrush</p>
        <h1 style={{color:C.dark,fontSize:24,fontWeight:"normal",margin:"0 0 28px",textAlign:"center",letterSpacing:-0.5}}>{authMode==="login"?"Welcome back":"Create account"}</h1>
        <div style={{marginBottom:16}}>
          <p style={{margin:"0 0 6px",fontSize:12,fontFamily:"sans-serif",color:C.mid}}>Email</p>
          <input type="email" value={authEmail} onChange={e=>setAuthEmail(e.target.value)} placeholder="you@email.com"
            onKeyDown={e=>e.key==="Enter"&&(authMode==="login"?handleSignIn():handleSignUp())}
            style={{width:"100%",border:"none",borderBottom:`2px solid ${C.border}`,outline:"none",fontSize:16,color:C.dark,background:"transparent",fontFamily:"'Georgia',serif",padding:"6px 0",boxSizing:"border-box"}}/>
        </div>
        <div style={{marginBottom:8,position:"relative"}}>
          <p style={{margin:"0 0 6px",fontSize:12,fontFamily:"sans-serif",color:C.mid}}>Password</p>
          <input type={showPassword?"text":"password"} value={authPassword} onChange={e=>setAuthPassword(e.target.value)} placeholder="••••••••"
            onKeyDown={e=>e.key==="Enter"&&(authMode==="login"?handleSignIn():handleSignUp())}
            style={{width:"100%",border:"none",borderBottom:`2px solid ${C.border}`,outline:"none",fontSize:16,color:C.dark,background:"transparent",fontFamily:"'Georgia',serif",padding:"6px 0 6px",boxSizing:"border-box",paddingRight:50}}/>
          <button onClick={()=>setShowPassword(p=>!p)} type="button"
            style={{position:"absolute",right:0,bottom:6,background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:12,fontFamily:"sans-serif",padding:"2px 4px"}}>
            {showPassword?"Hide":"Show"}
          </button>
        </div>
        {authError&&<p style={{margin:"8px 0",fontSize:12,fontFamily:"sans-serif",color:authError.includes("Check your email")?C.holiday:"#c9706a",lineHeight:1.4}}>{authError}</p>}
        <button onClick={authMode==="login"?handleSignIn:handleSignUp} disabled={authLoading||!authEmail||!authPassword}
          style={{width:"100%",background:authEmail&&authPassword?C.header:C.border,color:authEmail&&authPassword?C.accentText:C.faint,border:"none",borderRadius:12,padding:"14px",fontSize:15,fontFamily:"sans-serif",cursor:authEmail&&authPassword?"pointer":"default",marginTop:16,transition:"all 0.2s"}}>
          {authLoading?"...":(authMode==="login"?"Sign in":"Create account")}
        </button>
        <p style={{textAlign:"center",margin:"16px 0 0",fontSize:13,fontFamily:"sans-serif",color:C.muted}}>
          {authMode==="login"?"Don't have an account? ":"Already have an account? "}
          <button onClick={()=>{setAuthMode(authMode==="login"?"signup":"login");setAuthError("");}}
            style={{background:"none",border:"none",color:C.accent,cursor:"pointer",fontSize:13,fontFamily:"sans-serif",padding:0,textDecoration:"underline"}}>
            {authMode==="login"?"Sign up":"Sign in"}
          </button>
        </p>
        {authMode==="login"&&(
          <p style={{textAlign:"center",margin:"10px 0 0",fontSize:12,fontFamily:"sans-serif",color:C.faint}}>
            <button onClick={handleForgotPassword} style={{background:"none",border:"none",color:C.faint,cursor:"pointer",fontSize:12,fontFamily:"sans-serif",padding:0,textDecoration:"underline"}}>
              Forgot password?
            </button>
          </p>
        )}
        {forgotSent&&<p style={{textAlign:"center",margin:"8px 0 0",fontSize:12,fontFamily:"sans-serif",color:C.holiday,lineHeight:1.4}}>Check your email for a reset link!</p>}
        <p style={{textAlign:"center",margin:"20px 0 0",fontSize:11,fontFamily:"sans-serif",color:C.faint}}>App by Carrie ASMR ✨</p>
      </div>
    </div>
  );

  // ── Loading ────────────────────────────────────────
  if(!loaded) return(
    <div style={{minHeight:"100vh",background:C.page,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12}}>
      <div style={{width:36,height:36,border:`3px solid ${C.border}`,borderTop:`3px solid ${C.accent}`,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
      <p style={{fontFamily:"sans-serif",fontSize:13,color:C.muted,margin:0}}>Loading your data...</p>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // Calendar detail panel tint — derived from selWeek
  const calTintKey = selWeek?.tint||'lavender';
  const CT = selWeek&&isInWeek(selWeek.sunday,today)
    ? {header:C.header,label:C.accentDim,date:C.accentText,note:C.muted}
    : TINT_CFG[calTintKey]||TINT_CFG.lavender;

  const tabBtn=(t,label)=>(
    <button key={t} onClick={()=>switchTab(t)} style={{background:tab===t?C.page:"transparent",color:tab===t?C.dark:C.muted,border:"none",borderRadius:"10px 10px 0 0",padding:"10px 11px",fontSize:11,fontFamily:"sans-serif",letterSpacing:1,textTransform:"uppercase",cursor:"pointer",fontWeight:tab===t?"bold":"normal",transition:"all 0.2s"}}>{label}</button>
  );

  const headerSub=()=>{
    if(tab==="ideas")    return `${ideas.length-doneCount} idea${ideas.length-doneCount!==1?"s":""} available`;
    if(tab==="supplies") return `${needed.length} item${needed.length!==1?"s":""} needed`;
    if(tab==="todo")     return `${totalActive} active item${totalActive!==1?"s":""}`;
    if(tab==="notes")    return "Notes";
    if(tab==="calendar") return "Calendar";
    return "Planning board";
  };

  return(
    <div style={{minHeight:"100vh",background:C.page,fontFamily:"'Georgia',serif"}}>

      {/* ── Header ── */}
      <div style={{background:C.header,padding:"28px 24px 0",position:"sticky",top:0,zIndex:20,boxShadow:"0 2px 20px rgba(0,0,0,0.2)"}}>
        <div style={{maxWidth:680,margin:"0 auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div>
              <p style={{color:C.muted,fontSize:11,letterSpacing:3,textTransform:"uppercase",margin:"0 0 4px",fontFamily:"sans-serif"}}>ContentCrush</p>
              <h1 style={{color:C.accentText,fontSize:26,margin:"0 0 16px",fontWeight:"normal",letterSpacing:-0.5,minHeight:32}}>{headerSub()}</h1>
            </div>
            <button onClick={()=>setShowSettings(true)} style={{background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer",padding:"4px",marginTop:4,lineHeight:1}}>⚙️</button>
          </div>
          <div style={{display:"flex",flexWrap:"wrap"}}>
            {tabBtn("board","📋 Board")}
            {tabBtn("calendar","📅 Calendar")}
            {tabBtn("ideas","💡 Ideas")}
            {tabBtn("todo","✅ To-do")}
            {tabBtn("supplies","🛒 Supplies")}
            {tabBtn("notes","✏️ Notes")}
          </div>
        </div>
      </div>

      {/* ── Settings panel ── */}
      {showSettings&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:50,display:"flex"}}>
          <div onClick={()=>setShowSettings(false)} style={{position:"absolute",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.4)"}}/>
          <div style={{position:"absolute",top:0,right:0,bottom:0,width:"min(360px,100vw)",background:C.page,overflowY:"auto",boxShadow:"-4px 0 20px rgba(0,0,0,0.15)"}}>
            <div style={{background:C.header,padding:"28px 24px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <p style={{color:C.muted,fontSize:11,letterSpacing:3,textTransform:"uppercase",margin:"0 0 4px",fontFamily:"sans-serif"}}>ContentCrush</p>
                <h2 style={{color:C.accentText,fontSize:20,margin:0,fontWeight:"normal"}}>Preferences</h2>
              </div>
              <button onClick={()=>setShowSettings(false)} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",padding:0,lineHeight:1}}>×</button>
            </div>

            <div style={{padding:24}}>



              {/* Archive threshold */}
              <div style={{background:C.card,borderRadius:14,padding:18,marginBottom:16,boxShadow:"0 1px 8px rgba(0,0,0,0.06)"}}>
                <ColHeader style={{marginBottom:6}}>Auto-hide past weeks after</ColHeader>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                  <input type="number" min={0} max={8} value={archiveWeeks}
                    onChange={e=>setArchiveWeeks(Math.min(8,Math.max(0,parseInt(e.target.value)||0)))}
                    style={{width:60,border:`1px solid ${C.border}`,borderRadius:8,padding:"6px 10px",fontSize:16,fontFamily:"sans-serif",color:C.dark,background:C.page,outline:"none",textAlign:"center"}}/>
                  <span style={{fontSize:14,fontFamily:"sans-serif",color:C.mid}}>weeks</span>
                </div>
                <p style={{margin:0,fontSize:11,fontFamily:"sans-serif",color:C.faint,lineHeight:1.5}}>
                  Enter a number between 0 and 8 weeks. Older weeks are hidden from your Board to keep it clean — they're never deleted. Tap any past date in the Calendar to see your videos from that week.
                </p>
              </div>
              {/* How the app works — collapsible */}
              <div style={{background:`${C.accent}10`,borderRadius:14,marginBottom:16,border:`1px solid ${C.border}`,overflow:"hidden"}}>
                <button onClick={()=>setShowSettingsHelp(p=>!p)} style={{width:"100%",background:"none",border:"none",padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}}>
                  <ColHeader style={{margin:0}}>How this app works</ColHeader>
                  <span style={{fontSize:14,color:C.muted,transition:"transform 0.2s",transform:showSettingsHelp?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
                </button>
                {showSettingsHelp&&(
                  <div style={{padding:"0 18px 18px"}}>
                    {[
                      ["📋 Board","Your weekly video planning hub. Add weeks, log video names and prep notes, assign them to specific days. Each video has a circular progress indicator — tap it to move through stages: Created → Filmed → Edited → Posted. The circle fills 25% with each stage and fades quietly when posted. Tap any week header to collapse or expand it."],
                      ["📅 Calendar","See your plan by month. Tap any date to view that week's videos. Add 📌 single-day events or 📅 date ranges. Enable holiday overlays with 🌍."],
                      ["💡 Ideas","Capture ideas as they come. Tag them, add prep notes and supplies. Schedule to a week when ready, or flag for your to-do list."],
                      ["✅ To-do","Consolidated action list. Pulls prep notes from scheduled videos automatically. Also shows flagged ideas and manual items."],
                      ["🛒 Supplies","Track what to buy. Auto-populates from idea supplies. Check off when purchased."],
                      ["✏️ Notes","Freeform scratchpad. Anything on your mind. Auto-saves as you type."],
                    ].map(([title,desc],i,arr)=>(
                      <div key={title} style={{marginBottom:i<arr.length-1?10:0,paddingBottom:i<arr.length-1?10:0,borderBottom:i<arr.length-1?`1px solid ${C.border}`:"none"}}>
                        <p style={{margin:"0 0 2px",fontSize:13,fontFamily:"sans-serif",color:C.dark,fontWeight:"bold"}}>{title}</p>
                        <p style={{margin:0,fontSize:12,fontFamily:"sans-serif",color:C.mid,lineHeight:1.5}}>{desc}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{background:C.card,borderRadius:14,padding:18,marginBottom:16,boxShadow:"0 1px 8px rgba(0,0,0,0.06)"}}>
                <ColHeader style={{marginBottom:4}}>Idea tags</ColHeader>
                <p style={{margin:"0 0 14px",fontSize:12,fontFamily:"sans-serif",color:C.faint}}>Categories for your video ideas</p>
                {cats.map(cat=>(
                  <div key={cat.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                    {editingCatId===cat.id?(
                      <>
                        <input type="color" value={cat.color} onChange={e=>updCat(cat.id,{color:e.target.value})}
                          style={{width:28,height:28,border:"none",padding:0,cursor:"pointer",borderRadius:6,background:"none"}}/>
                        <input type="text" value={cat.label} onChange={e=>updCat(cat.id,{label:e.target.value})} autoFocus
                          onBlur={()=>setEditingCatId(null)} onKeyDown={e=>e.key==="Enter"&&setEditingCatId(null)}
                          style={{flex:1,border:"none",borderBottom:`2px solid ${C.accent}`,outline:"none",fontSize:14,color:C.dark,background:"transparent",fontFamily:"sans-serif",padding:"3px 0",boxSizing:"border-box"}}/>
                      </>
                    ):(
                      <>
                        <div style={{width:14,height:14,borderRadius:"50%",background:cat.color,flexShrink:0}}/>
                        <span style={{flex:1,fontSize:14,color:C.dark,fontFamily:"sans-serif"}}>{cat.label}</span>
                        <button onClick={()=>setEditingCatId(cat.id)} style={{background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",fontFamily:"sans-serif",padding:"2px 6px"}}>Edit</button>
                        <button onClick={()=>delCat(cat.id)} style={{background:"none",border:"none",color:C.faint,fontSize:16,cursor:"pointer",padding:2,lineHeight:1}}>×</button>
                      </>
                    )}
                  </div>
                ))}
                <div style={{display:"flex",gap:8,alignItems:"center",marginTop:12,paddingTop:12,borderTop:`1px solid ${C.border}`}}>
                  <input type="color" value={newCatColor} onChange={e=>setNewCatColor(e.target.value)}
                    style={{width:28,height:28,border:"none",padding:0,cursor:"pointer",borderRadius:6,background:"none"}}/>
                  <input type="text" placeholder="New idea tag..." value={newCatLabel} onChange={e=>setNewCatLabel(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&addCat()}
                    style={{flex:1,border:"none",borderBottom:`1px solid ${C.border}`,outline:"none",fontSize:14,color:C.dark,background:"transparent",fontFamily:"sans-serif",padding:"3px 0",boxSizing:"border-box"}}/>
                  <button onClick={addCat} disabled={!newCatLabel.trim()} style={{background:newCatLabel.trim()?C.header:C.border,color:newCatLabel.trim()?C.accentText:C.faint,border:"none",borderRadius:8,padding:"6px 14px",fontSize:13,fontFamily:"sans-serif",cursor:newCatLabel.trim()?"pointer":"default"}}>Add</button>
                </div>
              </div>

              {/* Event tags */}
              <div style={{background:C.card,borderRadius:14,padding:18,marginBottom:16,boxShadow:"0 1px 8px rgba(0,0,0,0.06)"}}>
                <ColHeader style={{marginBottom:4}}>Event tags</ColHeader>
                <p style={{margin:"0 0 14px",fontSize:12,fontFamily:"sans-serif",color:C.faint}}>Quick-fill labels for calendar events</p>
                {evtTags.map(tag=>(
                  <div key={tag.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                    {editingEvtTagId===tag.id?(
                      <>
                        <input type="color" value={tag.color} onChange={e=>updEvtTag(tag.id,{color:e.target.value})}
                          style={{width:28,height:28,border:"none",padding:0,cursor:"pointer",borderRadius:6,background:"none"}}/>
                        <input type="text" value={tag.label} onChange={e=>updEvtTag(tag.id,{label:e.target.value})} autoFocus
                          onBlur={()=>setEditingEvtTagId(null)} onKeyDown={e=>e.key==="Enter"&&setEditingEvtTagId(null)}
                          style={{flex:1,border:"none",borderBottom:`2px solid ${C.accent}`,outline:"none",fontSize:14,color:C.dark,background:"transparent",fontFamily:"sans-serif",padding:"3px 0",boxSizing:"border-box"}}/>
                      </>
                    ):(
                      <>
                        <div style={{width:14,height:14,borderRadius:"50%",background:tag.color,flexShrink:0}}/>
                        <span style={{flex:1,fontSize:14,color:C.dark,fontFamily:"sans-serif"}}>{tag.label}</span>
                        <button onClick={()=>setEditingEvtTagId(tag.id)} style={{background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",fontFamily:"sans-serif",padding:"2px 6px"}}>Edit</button>
                        <button onClick={()=>delEvtTag(tag.id)} style={{background:"none",border:"none",color:C.faint,fontSize:16,cursor:"pointer",padding:2,lineHeight:1}}>×</button>
                      </>
                    )}
                  </div>
                ))}
                <div style={{display:"flex",gap:8,alignItems:"center",marginTop:12,paddingTop:12,borderTop:`1px solid ${C.border}`}}>
                  <input type="color" value={newEvtTagColor} onChange={e=>setNewEvtTagColor(e.target.value)}
                    style={{width:28,height:28,border:"none",padding:0,cursor:"pointer",borderRadius:6,background:"none"}}/>
                  <input type="text" placeholder="New event tag..." value={newEvtTagLabel} onChange={e=>setNewEvtTagLabel(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&addEvtTag()}
                    style={{flex:1,border:"none",borderBottom:`1px solid ${C.border}`,outline:"none",fontSize:14,color:C.dark,background:"transparent",fontFamily:"sans-serif",padding:"3px 0",boxSizing:"border-box"}}/>
                  <button onClick={addEvtTag} disabled={!newEvtTagLabel.trim()} style={{background:newEvtTagLabel.trim()?C.header:C.border,color:newEvtTagLabel.trim()?C.accentText:C.faint,border:"none",borderRadius:8,padding:"6px 14px",fontSize:13,fontFamily:"sans-serif",cursor:newEvtTagLabel.trim()?"pointer":"default"}}>Add</button>
                </div>
              </div>

              {/* Admin panel — only visible to kerrcarrie5@gmail.com */}
              {user?.email===ADMIN_EMAIL&&(
                <AdminPanel supabase={supabase} C={C} ColHeader={ColHeader}/>
              )}

              <div style={{textAlign:"center",padding:"28px 0 8px"}}>
                <p style={{margin:0,fontSize:13,fontFamily:"sans-serif",color:C.muted}}>App by Carrie ASMR ✨</p>
                <p style={{margin:"4px 0 0",fontSize:12,fontFamily:"sans-serif",color:C.faint}}>crunchycrumbles5asmr@gmail.com</p>
                <p style={{margin:"6px 0 0",fontSize:11,fontFamily:"sans-serif",color:C.faint,opacity:0.5}}>Built with Claude · Anthropic</p>
                <button onClick={handleSignOut} style={{marginTop:16,background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 20px",fontSize:12,fontFamily:"sans-serif",color:C.muted,cursor:"pointer"}}>Sign out</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ BOARD TAB ══ */}
      {tab==="board"&&(
        <div style={{maxWidth:680,margin:"0 auto",padding:"24px 16px 120px"}}>
          {/* Board controls */}
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:12}}>
            <button onClick={()=>setHideEmptyWeeks(p=>!p)} style={{background:hideEmptyWeeks?C.header:"transparent",color:hideEmptyWeeks?C.accentText:C.muted,border:`1px solid ${hideEmptyWeeks?C.header:C.border}`,borderRadius:20,padding:"5px 14px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.2s"}}>
              {hideEmptyWeeks?"Showing weeks with videos":"Hide empty weeks"}
            </button>
          </div>
          {/* New here banner */}
          {showNewHereBanner&&(
            <div style={{background:`${C.accent}12`,border:`1px solid ${C.accent}30`,borderRadius:14,padding:"14px 16px",marginBottom:20,display:"flex",alignItems:"flex-start",gap:12,animation:"fadeIn 0.3s ease"}}>
              <span style={{fontSize:18,flexShrink:0}}>👋</span>
              <div style={{flex:1}}>
                <p style={{margin:"0 0 4px",fontSize:13,fontFamily:"sans-serif",color:C.dark,fontWeight:"500"}}>Welcome to ContentCrush!</p>
                <p style={{margin:0,fontSize:12,fontFamily:"sans-serif",color:C.mid,lineHeight:1.5}}>Add your first week below and start planning your videos. Tap ⚙️ → <em>How this app works</em> for a full guide anytime.</p>
              </div>
              <button onClick={()=>setShowNewHereBanner(false)} style={{background:"none",border:"none",color:C.faint,fontSize:18,cursor:"pointer",padding:0,lineHeight:1,flexShrink:0}}>×</button>
            </div>
          )}
          {(hideEmptyWeeks?visibleWeeks.filter(w=>w.entries.length>0||isInWeek(w.sunday,today)):visibleWeeks).map((week)=>{
            const isCurrentWeek=isInWeek(week.sunday,today);
            const isPastWeek=new Date(week.sunday)<archiveThreshold()&&!isCurrentWeek;
            const weekEvts=getWeekEvts(week.sunday);
            const tintKey = isCurrentWeek?'current':isPastWeek?'past':(week.tint||'lavender');
            const T = tintKey==='current'
              ? {header:C.header,label:C.accentDim,date:C.accentText,note:C.muted,chip:'rgba(255,255,255,0.2)'}
              : TINT_CFG[tintKey]||TINT_CFG.lavender;
            const miniCalRows = buildMiniCal(week.sunday, tintKey);
            return(
              <div key={week.id} ref={isCurrentWeek?currentWeekRef:null} style={{background:C.card,borderRadius:16,marginBottom:20,overflow:"hidden",boxShadow:"0 2px 16px rgba(0,0,0,0.07)",border:`2px solid ${isCurrentWeek?C.accent:"transparent"}`,opacity:isPastWeek?0.85:1}}>
                <div style={{background:T.header,padding:"14px 20px",cursor:"pointer"}} onClick={()=>toggleCollapse(week.id)}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                    <div style={{flex:1}}>
                      <p style={{fontSize:10,letterSpacing:2,textTransform:"uppercase",margin:"0 0 2px",fontFamily:"sans-serif",color:T.label}}>{isCurrentWeek?"Current week of":"Week of"}</p>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <p style={{fontSize:17,margin:0,color:T.date,fontWeight:"normal"}}>{formatWeekLabel(week.sunday)}</p>
                        <span style={{fontSize:11,fontFamily:"sans-serif",color:T.note,opacity:0.8}}>· {week.entries.length} {week.entries.length===1?"video":"videos"}</span>
                        {!isCurrentWeek&&!isPastWeek&&week.entries.length===0&&(
                          <button onClick={e=>{e.stopPropagation();setWeeks(p=>p.filter(w=>w.id!==week.id));}} style={{background:"none",border:"none",color:T.note,fontSize:14,cursor:"pointer",padding:"0 0 0 4px",opacity:0.6,lineHeight:1}}>×</button>
                        )}
                      </div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:5,flexShrink:0}}>
                      <div style={{display:"flex",flexDirection:"column",gap:2}}>{miniCalRows}</div>
                      <span style={{fontSize:10,color:T.label,transition:"transform 0.2s",display:"inline-block",transform:week.collapsed?"rotate(-90deg)":"rotate(0deg)"}}>▾</span>
                    </div>
                  </div>
                  {/* Week note — plain text field with quick chips */}
                  <input type="text" placeholder="Week note (e.g. holiday, travel)..." value={week.note||""} onChange={e=>updWeek(week.id,{note:e.target.value})}
                    style={{marginTop:10,width:"100%",background:"transparent",border:"none",borderBottom:"1px solid rgba(255,255,255,0.15)",outline:"none",fontSize:13,fontFamily:"sans-serif",color:T.note,padding:"4px 0",boxSizing:"border-box"}}/>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6}}>
                    {evtTags.map(tag=>(
                      <button key={tag.id} onClick={()=>updWeek(week.id,{note:tag.label})} style={{background:"transparent",border:`1px solid ${T.chip}`,borderRadius:12,padding:"2px 8px",fontSize:10,fontFamily:"sans-serif",color:T.note,cursor:"pointer"}}>{tag.label}</button>
                    ))}
                  </div>
                  {/* Read-only calendar events for this week */}
                  {weekEvts.length>0&&(
                    <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:8}}>
                      {weekEvts.map(ev=>(
                        <span key={ev.id} style={{display:"inline-flex",alignItems:"center",gap:4,background:`${C.event}22`,color:C.event,fontSize:11,fontFamily:"sans-serif",padding:"3px 8px",borderRadius:10}}>
                          {ev.span==="day"?"📌":"📅"} {ev.label}
                          {ev.span==="day"&&<span style={{opacity:0.7}}>· {formatShort(ev.date)}</span>}
                          {ev.span==="range"&&ev.endDate&&<span style={{opacity:0.7}}>· {formatShort(ev.date)} – {formatShort(ev.endDate)}</span>}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Read-only holiday reference layer */}
                  {(()=>{
                    if(!activeCountries.length) return null;
                    const weekHolidays = [];
                    for(let i=0;i<7;i++){
                      const d=new Date(week.sunday);
                      d.setDate(d.getDate()+i);
                      const dayHols = getHolidaysForDate(d);
                      dayHols.forEach(h=>{
                        if(!weekHolidays.find(x=>x.name===h.name)){
                          weekHolidays.push({...h, displayDate: d});
                        }
                      });
                    }
                    if(!weekHolidays.length) return null;
                    return (
                      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:6,paddingTop:6,borderTop:"1px solid rgba(255,255,255,0.1)"}}>
                        {weekHolidays.map((h,i)=>(
                          <span key={i} style={{display:"inline-flex",alignItems:"center",gap:4,background:`${C.holiday}18`,color:C.holiday,fontSize:11,fontFamily:"sans-serif",padding:"3px 8px",borderRadius:10}}>
                            🌍 {h.name}
                            <span style={{opacity:0.7}}>· {formatShort(h.displayDate)}</span>
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                {!week.collapsed&&(week.entries.length>0||week.isWriting)&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",padding:"10px 20px 6px",borderBottom:`1px solid ${C.border}`,background:isCurrentWeek?"transparent":`${T.header}20`}}>
                    <ColHeader>Video name</ColHeader>
                    <ColHeader style={{paddingLeft:10}}>Prep notes</ColHeader>
                  </div>
                )}
                {!week.collapsed&&week.entries.map(entry=>(
                  <div key={entry.id} style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,position:"relative",opacity:1,background:(entry.stage||0)===4?"rgba(0,0,0,0.02)":isCurrentWeek?"transparent":`${T.header}28`,transition:"background 0.4s"}}>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",marginBottom:10}}>
                      <div style={{paddingRight:10}}>
                        <InlineEdit value={entry.videoName||""} placeholder="Untitled" onChange={v=>updEntry(week.id,entry.id,{videoName:v})}
                          style={{fontSize:15,lineHeight:1.5,color:(entry.stage||0)===4?C.muted:C.dark,fontWeight:(entry.stage||0)===4?"normal":"bold",fontFamily:"'Georgia',serif",display:"block",transition:"color 0.4s"}}/>
                      </div>
                      <div style={{paddingLeft:10,borderLeft:`1px solid ${C.border}`}}>
                        <InlineEdit value={entry.prep||""} placeholder="No notes" multiline onChange={v=>updEntry(week.id,entry.id,{prep:v})}
                          style={{fontSize:15,lineHeight:1.6,color:C.mid,fontFamily:"'Georgia',serif",display:"block",whiteSpace:"pre-wrap"}}/>
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                      <span style={{fontSize:10,letterSpacing:1,textTransform:"uppercase",fontFamily:"sans-serif",color:C.faint,marginRight:2}}>{entry.day?"Assigned:":"Assign day:"}</span>
                      {DAYS.map(d=>(
                        <button key={d} onClick={()=>assignDay(week.id,entry.id,d)} style={{background:entry.day===d?C.header:"transparent",color:entry.day===d?C.accentText:C.faint,border:`1.5px solid ${entry.day===d?C.header:C.border}`,borderRadius:6,padding:"3px 7px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s",fontWeight:entry.day===d?"bold":"normal"}}>{d}</button>
                      ))}
                      {/* Stage indicator — circular donut */}
                      {(()=>{
                        const stage = STAGES[entry.stage||0];
                        const C_CIRC = 87.96;
                        const isPosted = (entry.stage||0)===4;
                        return (
                          <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8,cursor:"pointer"}} onClick={(e)=>{e.stopPropagation();advanceStage(week.id,entry.id);}}>
                            <div style={{position:"relative",width:28,height:28,flexShrink:0}}>
                              <svg width="28" height="28" viewBox="0 0 36 36" style={{transform:"rotate(-90deg)"}}>
                                <circle cx="18" cy="18" r="14" fill="none" stroke="#eaecf1" strokeWidth="3.5"/>
                                <circle cx="18" cy="18" r="14" fill="none" stroke={stage.color} strokeWidth="3.5"
                                  strokeLinecap="round" strokeDasharray={C_CIRC} strokeDashoffset={stage.offset}
                                  style={{transition:"stroke-dashoffset 0.4s ease,stroke 0.4s ease"}}/>
                              </svg>
                              {isPosted&&<span style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",fontSize:9,color:C.faint,fontWeight:"normal",lineHeight:1}}>✓</span>}
                            </div>
                            <div style={{display:"flex",flexDirection:"column",gap:1}}>
                              <span style={{fontSize:11,fontFamily:"sans-serif",fontWeight:"500",color:stage.color,transition:"color 0.3s"}}>{stage.label}</span>
                              <span style={{fontSize:9,fontFamily:"sans-serif",color:C.faint}}>{stage.hint}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    <button onClick={()=>delEntry(week.id,entry.id)} style={{position:"absolute",top:10,right:10,background:"none",border:"none",color:C.faint,fontSize:18,cursor:"pointer",padding:4,lineHeight:1,zIndex:2}}>×</button>
                  </div>
                ))}
                {!week.collapsed&&(week.isWriting?<EntryForm week={week} updWeek={updWeek} cancelDraft={cancelDraft} saveEntry={saveEntry} draftRef={draftRef}/>:(
                  <div style={{padding:"12px 20px"}}><DashedBtn onClick={()=>startWriting(week.id)}>+ Add video</DashedBtn></div>
                ))}
              </div>
            );
          })}
          {archivedWeeks.length>0&&(
            <div style={{textAlign:"center",marginBottom:16}}>
              <button onClick={()=>setShowArchived(p=>!p)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:20,padding:"6px 16px",fontSize:12,fontFamily:"sans-serif",color:C.muted,cursor:"pointer"}}>
                {showArchived?"Hide":"Show"} {archivedWeeks.length} archived week{archivedWeeks.length!==1?"s":""}
              </button>
            </div>
          )}
          {showArchived&&archivedWeeks.map(week=>(
            <div key={week.id} style={{background:C.card,borderRadius:16,marginBottom:12,overflow:"hidden",boxShadow:"0 1px 8px rgba(0,0,0,0.04)",opacity:0.7,border:`1px solid ${C.border}`}}>
              <div style={{background:TINT_CFG.past.header,padding:"10px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <p style={{fontSize:9,letterSpacing:2,textTransform:"uppercase",margin:"0 0 2px",fontFamily:"sans-serif",color:TINT_CFG.past.label}}>Archived · Week of</p>
                  <p style={{fontSize:15,margin:0,color:TINT_CFG.past.date}}>{formatWeekLabel(week.sunday)}</p>
                </div>
                <span style={{fontFamily:"sans-serif",fontSize:11,color:TINT_CFG.past.label}}>{week.entries.length} video{week.entries.length!==1?"s":""}</span>
              </div>
              {week.entries.map(entry=>(
                <div key={entry.id} style={{padding:"10px 20px",borderBottom:`1px solid ${C.border}`}}>
                  <p style={{margin:0,fontSize:14,color:C.muted,fontWeight:"bold"}}>{entry.videoName||"Untitled"}</p>
                  {entry.prep&&<p style={{margin:"4px 0 0",fontSize:13,color:C.faint}}>{entry.prep}</p>}
                </div>
              ))}
            </div>
          ))}
          <div ref={bottomRef} style={{height:1}}/>
        </div>
      )}

      {/* ══ CALENDAR TAB ══ */}
      {tab==="calendar"&&(
        <div style={{maxWidth:680,margin:"0 auto",padding:"20px 12px 40px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,padding:"0 4px"}}>
            <button onClick={prevMonth} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,width:40,height:40,fontSize:18,cursor:"pointer",color:C.dark,display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
            <h2 style={{margin:0,fontSize:20,fontWeight:"normal",color:C.dark}}>{MONTHS[calMonth]} {calYear}</h2>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <button onClick={()=>setShowHolidayPicker(p=>!p)} style={{background:activeCountries.length?C.holiday:"transparent",color:activeCountries.length?"#fff":C.muted,border:`1px solid ${activeCountries.length?C.holiday:C.border}`,borderRadius:8,padding:"6px 10px",fontSize:12,fontFamily:"sans-serif",cursor:"pointer"}}>
                🌍{activeCountries.length>0?` ${activeCountries.length}`:""}
              </button>
              <button onClick={nextMonth} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,width:40,height:40,fontSize:18,cursor:"pointer",color:C.dark,display:"flex",alignItems:"center",justifyContent:"center"}}>›</button>
            </div>
          </div>

          {/* Calendar help tip */}
          <div style={{marginBottom:12}}>
            <button onClick={()=>setShowCalHelp(p=>!p)} style={{background:"none",border:"none",padding:0,display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}>
              <span style={{fontSize:12,fontFamily:"sans-serif",color:C.muted}}>💡 How to add events</span>
              <span style={{fontSize:11,color:C.faint,transition:"transform 0.2s",transform:showCalHelp?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
            </button>
            {showCalHelp&&(
              <div style={{marginTop:8,background:C.card,borderRadius:12,padding:"12px 16px",boxShadow:"0 1px 6px rgba(0,0,0,0.06)"}}>
                <p style={{margin:"0 0 8px",fontSize:12,fontFamily:"sans-serif",color:C.dark,lineHeight:1.5}}><strong>📌 Single day</strong> — Tap any date, then tap "+ Add event or note." Pick "This day," add a label, save.</p>
                <p style={{margin:"0 0 8px",fontSize:12,fontFamily:"sans-serif",color:C.dark,lineHeight:1.5}}><strong>📅 Date range</strong> — Tap a date, tap "+ Add event or note," then tap "Date range →." A banner will guide you: tap your start date on the calendar, then tap your end date. Use ‹ › to change months between taps if your range spans months. Once both dates are picked, name the event and save.</p>
                <p style={{margin:0,fontSize:12,fontFamily:"sans-serif",color:C.dark,lineHeight:1.5}}><strong>🌍 Holidays</strong> — Tap the 🌍 button to choose which countries' public holidays to show as reference markers on the calendar.</p>
              </div>
            )}
          </div>

          {showHolidayPicker&&(
            <div style={{background:C.card,borderRadius:14,padding:16,marginBottom:16,boxShadow:"0 2px 12px rgba(0,0,0,0.08)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <ColHeader>Show holidays for</ColHeader>
                <button onClick={()=>setShowHolidayPicker(false)} style={{background:"none",border:"none",color:C.faint,fontSize:18,cursor:"pointer",padding:0}}>×</button>
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {ALL_COUNTRIES.map(c=>(
                  <button key={c.code} onClick={()=>toggleCountry(c.code)} style={{background:activeCountries.includes(c.code)?C.holiday:"transparent",color:activeCountries.includes(c.code)?"#fff":C.mid,border:`1.5px solid ${activeCountries.includes(c.code)?C.holiday:C.border}`,borderRadius:16,padding:"5px 12px",fontSize:12,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>{c.name}</button>
                ))}
              </div>
            </div>
          )}

          {rangePickStep>0&&(
            <div style={{background:`${C.event}18`,borderRadius:10,padding:"10px 16px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <p style={{margin:0,fontSize:13,fontFamily:"sans-serif",color:C.event}}>
                {rangePickStep===1?"Tap the start date":"Tap the end date · Start: "+formatShort(evtRangeStart)}
              </p>
              <button onClick={()=>{setRangePickStep(0);setEvtRangeStart(null);setEvtRangeEnd(null);}} style={{background:"none",border:"none",color:C.event,cursor:"pointer",fontSize:13,fontFamily:"sans-serif"}}>Cancel</button>
            </div>
          )}

          {/* Month observances — filtered by active countries */}
          {(()=>{
            const obs = getMonthObservances(calYear, calMonth);
            if(!obs.length||!activeCountries.length) return null;
            // Filter each observance's countries to only those the user has selected
            const filtered = obs.map(o=>{
              // Parse country list from the string — match against ALL_COUNTRIES names
              const matchedNames = ALL_COUNTRIES
                .filter(c=>activeCountries.includes(c.code))
                .map(c=>c.name)
                .filter(name=>o.countries.includes(name));
              // For global ones like "Global Muslim", "Global Christian" always show
              const isGlobal = o.countries.includes("Global");
              if(!matchedNames.length && !isGlobal) return null;
              const displayCountries = isGlobal ? o.countries : matchedNames.join(", ");
              return {...o, displayCountries};
            }).filter(Boolean);
            if(!filtered.length) return null;
            return (
              <div style={{marginBottom:10}}>
                {filtered.map((o,i)=>(
                  <p key={i} style={{margin:"0 0 4px",fontSize:12,fontFamily:"sans-serif",color:C.mid,lineHeight:1.4}}>
                    {o.label} <span style={{color:C.faint}}>· {o.displayCountries}</span>
                  </p>
                ))}
              </div>
            );
          })()}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",marginBottom:4}}>
            {DAYS.map(d=><div key={d} style={{textAlign:"center",padding:"6px 0",fontSize:10,letterSpacing:1,textTransform:"uppercase",fontFamily:"sans-serif",color:C.muted}}>{d}</div>)}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:20}}>
            {grid.map(({date,thisMonth},i)=>{
              const isToday     = isSameDay(date,today);
              const isSel       = !!selDate&&isSameDay(date,selDate);
              const isSun       = date.getDay()===0;
              const dayName     = DAYS[date.getDay()];
              const assignedCount = thisMonth ? weeks.reduce((sum,w)=>isInWeek(w.sunday,date)?sum+w.entries.filter(e=>e.day===dayName).length:sum, 0) : 0;
              const weekHasVids = thisMonth&&isSun&&weeks.some(w=>isSameDay(w.sunday,date)&&w.entries.length>0);
              const weekNote    = thisMonth&&isSun?weeks.find(w=>isSameDay(w.sunday,date))?.note:null;
              const dayEvts     = thisMonth?getEvtsForDate(date).filter(e=>e.span==="day"):[];
              const rangeEvts   = thisMonth?calEvts.filter(e=>e.span==="range"&&e.endDate&&isInRange(e.date,e.endDate,date)):[];
              const hasRangeEvt = rangeEvts.length>0;
              const isRangeStart= rangeEvts.some(e=>isSameDay(e.date,date));
              const dayHolidays = thisMonth?getHolidaysForDate(date):[];
              const weekEvtBg   = hasRangeEvt&&!isSel?"rgba(200,170,100,0.10)":null;

              // Range picking visual state
              const isPickStart  = rangePickStep===1&&evtRangeStart&&thisMonth&&isSameDay(date,evtRangeStart);
              const isPickEnd    = rangePickStep===2&&evtRangeEnd&&thisMonth&&isSameDay(date,evtRangeEnd);
              const isPickAnchor = rangePickStep===2&&evtRangeStart&&thisMonth&&isSameDay(date,evtRangeStart);
              const isInPickRange= rangePickStep===2&&evtRangeStart&&evtRangeEnd&&thisMonth&&isInRange(evtRangeStart,evtRangeEnd,date);

              return(
                <div key={i} onClick={()=>thisMonth&&handleDateTap(date)} style={{
                  minHeight:68,borderRadius:10,padding:"5px 4px 4px",cursor:thisMonth?"pointer":"default",position:"relative",overflow:"hidden",
                  background: isSel?C.header
                    : isPickAnchor?C.accent
                    : isInPickRange?`${C.accent}25`
                    : weekEvtBg||(isToday?`${C.accent}12`:weekHasVids?`${C.accent}08`:C.card),
                  border: isSel?`2px solid ${C.accent}`
                    : isPickAnchor||isPickEnd?`2px solid ${C.accent}`
                    : isInPickRange?`2px solid ${C.accent}55`
                    : isToday?`2px solid ${C.accent}`:"2px solid transparent",
                  borderLeft:!isSel&&!isToday&&!isPickAnchor&&!isInPickRange&&weekHasVids?`3px solid ${C.accent}`:undefined,
                  opacity:thisMonth?1:0.3,transition:"all 0.15s",
                }}>
                  {hasRangeEvt&&!isSel&&!isPickAnchor&&!isInPickRange&&<div style={{position:"absolute",top:0,left:0,right:0,height:2.5,background:"#c8aa64",opacity:0.6}}/>}
                  {dayHolidays.length>0&&!isSel&&<div style={{position:"absolute",bottom:0,left:0,right:0,height:2,background:C.holiday,opacity:0.7}}/>}
                  <p style={{margin:0,fontSize:13,fontFamily:"sans-serif",fontWeight:isToday||isSun?"bold":"normal",textAlign:"center",
                    color: isSel||isPickAnchor?C.accentText
                      : isInPickRange?C.accent
                      : isToday?C.accent
                      : isSun&&thisMonth?C.accentDim
                      : thisMonth?C.dark:"#bbb"}}>
                    {date.getDate()}
                  </p>
                  {/* Range picking labels */}
                  {isPickAnchor&&(
                    <p style={{margin:"1px 0 0",fontSize:7,fontFamily:"sans-serif",color:C.accentText,textAlign:"center",fontWeight:"bold"}}>Start</p>
                  )}
                  {isPickEnd&&!isPickAnchor&&(
                    <p style={{margin:"1px 0 0",fontSize:7,fontFamily:"sans-serif",color:C.accent,textAlign:"center",fontWeight:"bold"}}>End</p>
                  )}
                  {/* Saved range event labels */}
                  {isRangeStart&&thisMonth&&!isPickAnchor&&rangeEvts.map(ev=>(
                    <p key={ev.id} style={{margin:"1px 0 0",fontSize:7,fontFamily:"sans-serif",color:isSel?C.accentText:"#a07820",textAlign:"center",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",padding:"0 2px",fontWeight:"bold"}}>{ev.label}</p>
                  ))}
                  {weekNote&&!hasRangeEvt&&isSun&&(
                    <p style={{margin:"2px 0 0",fontSize:7,fontFamily:"sans-serif",color:isSel?C.accentText:C.muted,textAlign:"center",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",padding:"0 2px"}}>{weekNote}</p>
                  )}
                  {dayEvts.map(ev=>(
                    <div key={ev.id} style={{margin:"2px 1px 0",background:isSel?`${C.accent}33`:`${C.event}18`,borderRadius:3,padding:"1px 3px"}}>
                      <p style={{margin:0,fontSize:7,fontFamily:"sans-serif",color:isSel?C.accentText:C.event,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>📌 {ev.label}</p>
                    </div>
                  ))}
                  {dayHolidays.length>0&&(
                    <div style={{position:"absolute",bottom:4,right:4}}>
                      <div style={{width:5,height:5,borderRadius:"50%",background:isSel?C.accentText:C.holiday}}/>
                    </div>
                  )}
                  {assignedCount>0&&(()=>{
                    const dayWeek = weeks.find(w=>isInWeek(w.sunday,date));
                    const badgeTint = dayWeek?.tint||'lavender';
                    const badgeColor = isSel?C.accentText:(TINT_CFG[badgeTint]?.calActive||C.accent);
                    return(
                      <div style={{position:"absolute",bottom:4,left:4}}>
                        <div style={{background:badgeColor,color:"#fff",borderRadius:6,padding:"0px 4px",fontSize:8,fontFamily:"sans-serif",fontWeight:"bold",lineHeight:"14px",minWidth:14,textAlign:"center"}}>{assignedCount}</div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>

          {selDate&&(
            <div style={{background:C.card,borderRadius:16,overflow:"hidden",boxShadow:"0 2px 16px rgba(0,0,0,0.08)",animation:"fadeIn 0.2s ease"}}>
              <div style={{background:CT.header,padding:"14px 20px"}}>
                <p style={{margin:0,fontSize:10,letterSpacing:2,textTransform:"uppercase",fontFamily:"sans-serif",color:CT.label}}>{selDate.toLocaleDateString("en-US",{weekday:"long"})}</p>
                <p style={{margin:"2px 0 0",fontSize:18,color:CT.date,fontWeight:"normal"}}>{selDate.toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</p>
                {selWeek&&<p style={{margin:"4px 0 0",fontSize:11,color:CT.note,fontFamily:"sans-serif"}}>Week of {formatWeekLabel(selWeek.sunday)}</p>}
                {selWeek?.note&&(
                  <div style={{marginTop:8,display:"inline-flex",alignItems:"center",gap:6,background:`${C.highlight}22`,borderRadius:8,padding:"5px 10px"}}>
                    <span style={{fontSize:12}}>📌</span>
                    <span style={{fontSize:13,color:C.accentText,fontFamily:"sans-serif"}}>{selWeek.note}</span>
                  </div>
                )}
                {selHolidays.length>0&&(
                  <div style={{marginTop:8,display:"flex",flexWrap:"wrap",gap:6}}>
                    {selHolidays.map((h,hi)=>(
                      <div key={hi} style={{display:"inline-flex",alignItems:"center",gap:5,background:`${C.holiday}22`,borderRadius:8,padding:"5px 10px"}}>
                        <span style={{fontSize:11}}>🌍</span>
                        <span style={{fontSize:12,color:C.holiday,fontFamily:"sans-serif"}}>{h.name} <span style={{opacity:0.6}}>· {(h.matchedCountries||h.countries||[]).map(c=>ALL_COUNTRIES.find(x=>x.code===c)?.name||c).join(", ")}</span></span>
                      </div>
                    ))}
                  </div>
                )}
                {selEvts.length>0&&(
                  <div style={{marginTop:8,display:"flex",flexWrap:"wrap",gap:6}}>
                    {selEvts.map(ev=>(
                      <div key={ev.id} style={{display:"inline-flex",alignItems:"center",gap:6,background:`${C.accent}25`,borderRadius:8,padding:"5px 10px"}}>
                        <span style={{fontSize:11}}>{ev.span==="day"?"📌":"📅"}</span>
                        <div>
                          <span style={{fontSize:13,color:C.accentDim,fontFamily:"sans-serif"}}>{ev.label}</span>
                          {ev.endDate&&<span style={{fontSize:11,color:C.muted,fontFamily:"sans-serif",marginLeft:6}}>{formatShort(ev.date)} – {formatShort(ev.endDate)}</span>}
                          {ev.detail&&<span style={{fontSize:11,color:C.muted,fontFamily:"sans-serif",marginLeft:6}}>— {ev.detail}</span>}
                        </div>
                        <button onClick={()=>delEvt(ev.id)} style={{background:"none",border:"none",color:C.muted,fontSize:14,cursor:"pointer",padding:0,lineHeight:1}}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add event — calendar only */}
              {addingEvt?(
                <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,background:C.highlightSoft}}>
                  <ColHeader style={{marginBottom:10}}>New event</ColHeader>
                  {/* Event tag quick-fill chips */}
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
                    {evtTags.map(tag=>(
                      <button key={tag.id} onClick={()=>setEvtLabel(tag.label)} style={{background:evtLabel===tag.label?tag.color:"transparent",color:evtLabel===tag.label?"#fff":tag.color,border:`1.5px solid ${tag.color}`,borderRadius:16,padding:"3px 10px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>{tag.label}</button>
                    ))}
                  </div>
                  <input autoFocus type="text" placeholder="Event label..." value={evtLabel} onChange={e=>setEvtLabel(e.target.value)}
                    style={{width:"100%",border:"none",borderBottom:`2px solid ${C.accent}`,outline:"none",fontSize:15,color:C.dark,background:"transparent",fontFamily:"'Georgia',serif",padding:"4px 0 6px",boxSizing:"border-box",marginBottom:10}}/>
                  <input type="text" placeholder="Details (optional)" value={evtDetail} onChange={e=>setEvtDetail(e.target.value)}
                    style={{width:"100%",border:"none",borderBottom:`1px solid ${C.border}`,outline:"none",fontSize:14,color:C.mid,background:"transparent",fontFamily:"'Georgia',serif",padding:"4px 0 6px",boxSizing:"border-box",marginBottom:12}}/>
                  <div style={{display:"flex",gap:8,marginBottom:12}}>
                    <button onClick={()=>setEvtSpan("day")} style={{background:evtSpan==="day"?C.header:"transparent",color:evtSpan==="day"?C.accentText:C.muted,border:`1px solid ${evtSpan==="day"?C.header:C.border}`,borderRadius:20,padding:"5px 14px",fontSize:12,fontFamily:"sans-serif",cursor:"pointer"}}>📌 This day</button>
                    <button onClick={()=>{setEvtSpan("range");setAddingEvt(false);setRangePickStep(1);}} style={{background:"transparent",color:C.muted,border:`1px solid ${C.border}`,borderRadius:20,padding:"5px 14px",fontSize:12,fontFamily:"sans-serif",cursor:"pointer"}}>📅 Date range →</button>
                  </div>
                  <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                    <CancelBtn onClick={()=>{setAddingEvt(false);setEvtLabel("");setEvtDetail("");}}/>
                    <button onClick={saveEvt} disabled={!evtLabel.trim()||evtSpan==="range"} style={{background:evtLabel.trim()&&evtSpan!=="range"?C.accent:C.border,color:evtLabel.trim()&&evtSpan!=="range"?"#fff":C.faint,border:"none",borderRadius:8,padding:"7px 16px",fontSize:13,fontFamily:"sans-serif",cursor:evtLabel.trim()&&evtSpan!=="range"?"pointer":"default"}}>Save event</button>
                  </div>
                </div>
              ):(
                <div style={{padding:"10px 20px",borderBottom:`1px solid ${C.border}`}}>
                  <DashedBtn onClick={()=>setAddingEvt(true)} color={C.event}>+ Add event or note</DashedBtn>
                </div>
              )}

              {/* Range label input after dates picked */}
              {evtSpan==="range"&&evtRangeStart&&evtRangeEnd&&!addingEvt&&(
                <div style={{padding:"14px 20px",background:C.highlightSoft,borderBottom:`1px solid ${C.border}`}}>
                  <ColHeader style={{marginBottom:8}}>Name this event</ColHeader>
                  <p style={{margin:"0 0 10px",fontSize:12,fontFamily:"sans-serif",color:C.muted}}>{formatShort(evtRangeStart)} – {formatShort(evtRangeEnd)}</p>
                  {/* Event tag quick-fill for range */}
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
                    {evtTags.map(tag=>(
                      <button key={tag.id} onClick={()=>setEvtLabel(tag.label)} style={{background:evtLabel===tag.label?tag.color:"transparent",color:evtLabel===tag.label?"#fff":tag.color,border:`1.5px solid ${tag.color}`,borderRadius:16,padding:"3px 10px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>{tag.label}</button>
                    ))}
                  </div>
                  <input autoFocus type="text" placeholder="Event label (e.g. Trip to Austin)..." value={evtLabel} onChange={e=>setEvtLabel(e.target.value)}
                    style={{width:"100%",border:"none",borderBottom:`2px solid ${C.accent}`,outline:"none",fontSize:15,color:C.dark,background:"transparent",fontFamily:"'Georgia',serif",padding:"4px 0 6px",boxSizing:"border-box",marginBottom:12}}/>
                  <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                    <CancelBtn onClick={()=>{setEvtRangeStart(null);setEvtRangeEnd(null);setEvtLabel("");setEvtSpan("day");}}/>
                    <button onClick={saveEvt} disabled={!evtLabel.trim()} style={{background:evtLabel.trim()?C.accent:C.border,color:evtLabel.trim()?"#fff":C.faint,border:"none",borderRadius:8,padding:"7px 16px",fontSize:13,fontFamily:"sans-serif",cursor:evtLabel.trim()?"pointer":"default"}}>Save range</button>
                  </div>
                </div>
              )}

              {selEntries.length===0&&<div style={{padding:"16px 20px",textAlign:"center"}}><p style={{color:C.faint,fontStyle:"italic",fontSize:15,margin:0}}>No videos planned for this week</p></div>}
              {selEntries.length>0&&(
                <div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",padding:"10px 20px 6px",borderBottom:`1px solid ${C.border}`,background:`${CT.header}20`}}>
                    <ColHeader>Video name</ColHeader><ColHeader style={{paddingLeft:10}}>Prep notes</ColHeader>
                  </div>
                  {selEntries.map((entry,ei)=>(
                    <div key={entry.id} style={{padding:"14px 20px",borderBottom:ei<selEntries.length-1?`1px solid ${C.border}`:"none",position:"relative",background:(entry.stage||0)===4?"rgba(0,0,0,0.02)":`${CT.header}28`,transition:"background 0.4s"}}>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",marginBottom:10}}>
                        <div style={{paddingRight:10}}>
                          <InlineEdit value={entry.videoName||""} placeholder="Untitled" onChange={v=>updEntry(selWeek.id,entry.id,{videoName:v})}
                            style={{fontSize:15,lineHeight:1.5,color:(entry.stage||0)===4?C.muted:C.dark,fontWeight:(entry.stage||0)===4?"normal":"bold",fontFamily:"'Georgia',serif",display:"block"}}/>
                        </div>
                        <div style={{paddingLeft:10,borderLeft:`1px solid ${C.border}`}}>
                          <InlineEdit value={entry.prep||""} placeholder="No notes" multiline onChange={v=>updEntry(selWeek.id,entry.id,{prep:v})}
                            style={{fontSize:15,lineHeight:1.6,color:C.mid,fontFamily:"'Georgia',serif",display:"block",whiteSpace:"pre-wrap"}}/>
                        </div>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                        <span style={{fontSize:10,letterSpacing:1,textTransform:"uppercase",fontFamily:"sans-serif",color:C.faint,marginRight:2}}>{entry.day?"Assigned:":"Assign day:"}</span>
                        {DAYS.map(d=>(
                          <button key={d} onClick={()=>assignDay(selWeek.id,entry.id,d)} style={{background:entry.day===d?CT.header:"transparent",color:entry.day===d?"#fff":C.faint,border:`1.5px solid ${entry.day===d?CT.header:C.border}`,borderRadius:6,padding:"3px 7px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>{d}</button>
                        ))}
                        {(()=>{
                          const stage=STAGES[entry.stage||0];
                          const C_CIRC=87.96;
                          const isPosted=(entry.stage||0)===4;
                          return(
                            <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8,cursor:"pointer"}} onClick={()=>advanceStage(selWeek.id,entry.id)}>
                              <div style={{position:"relative",width:28,height:28,flexShrink:0}}>
                                <svg width="28" height="28" viewBox="0 0 36 36" style={{transform:"rotate(-90deg)"}}>
                                  <circle cx="18" cy="18" r="14" fill="none" stroke="#eaecf1" strokeWidth="3.5"/>
                                  <circle cx="18" cy="18" r="14" fill="none" stroke={stage.color} strokeWidth="3.5"
                                    strokeLinecap="round" strokeDasharray={C_CIRC} strokeDashoffset={stage.offset}
                                    style={{transition:"stroke-dashoffset 0.4s ease,stroke 0.4s ease"}}/>
                                </svg>
                                {isPosted&&<span style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",fontSize:9,color:C.faint,lineHeight:1}}>✓</span>}
                              </div>
                              <div style={{display:"flex",flexDirection:"column",gap:1}}>
                                <span style={{fontSize:11,fontFamily:"sans-serif",fontWeight:"500",color:stage.color}}>{stage.label}</span>
                                <span style={{fontSize:9,fontFamily:"sans-serif",color:C.faint}}>{stage.hint}</span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      <button onClick={()=>delEntry(selWeek.id,entry.id)} style={{position:"absolute",top:10,right:10,background:"none",border:"none",color:C.faint,fontSize:18,cursor:"pointer",padding:4,lineHeight:1}}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {selWeek&&<div style={{padding:"12px 20px"}}><DashedBtn onClick={()=>{setTab("board");setTimeout(()=>startWriting(selWeek.id),100);}}>+ Add video</DashedBtn></div>}
            </div>
          )}
          {!selDate&&<p style={{textAlign:"center",color:C.faint,fontStyle:"italic",fontSize:14,marginTop:8}}>
            {rangePickStep>0?"Use ‹ › to navigate months while picking dates":"Tap any date to see details"}
          </p>}
        </div>
      )}

      {/* ══ IDEAS TAB ══ */}
      {tab==="ideas"&&(
        <div style={{maxWidth:680,margin:"0 auto",padding:"24px 16px 40px"}}>
          {showNewIdeaForm?(
          <div style={{background:C.card,borderRadius:16,padding:20,marginBottom:20,boxShadow:"0 2px 16px rgba(0,0,0,0.07)",animation:"fadeIn 0.2s ease"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <ColHeader>New idea</ColHeader>
              <button onClick={()=>{setShowNewIdeaForm(false);setIdeaText("");setIdeaPrep("");setIdeaSupply("");setIdeaCat("");}} style={{background:"none",border:"none",color:C.faint,fontSize:18,cursor:"pointer",padding:0,lineHeight:1}}>×</button>
            </div>
            <textarea ref={ideaRef} placeholder="What's the video idea?" value={ideaText} onChange={e=>setIdeaText(e.target.value)} rows={2}
              style={{width:"100%",border:"none",borderBottom:`2px solid ${C.border}`,outline:"none",resize:"none",fontSize:16,lineHeight:1.6,color:C.dark,background:"transparent",fontFamily:"'Georgia',serif",padding:"0 0 8px",boxSizing:"border-box",marginBottom:14}}/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:14}}>
              <div>
                <ColHeader style={{marginBottom:6}}>Prep notes</ColHeader>
                <textarea placeholder="What do I need to do..." value={ideaPrep} onChange={e=>setIdeaPrep(e.target.value)} rows={3}
                  style={{width:"100%",border:"none",borderBottom:`2px solid ${C.border}`,outline:"none",resize:"none",fontSize:14,lineHeight:1.6,color:C.mid,background:"transparent",fontFamily:"'Georgia',serif",padding:"0 0 6px",boxSizing:"border-box"}}/>
              </div>
              <div>
                <ColHeader style={{marginBottom:6,color:C.event}}>Supplies needed <span style={{fontSize:9,textTransform:"none",color:C.faint}}>(auto-added)</span></ColHeader>
                <textarea placeholder="e.g. fresh chalk, dye... (comma separated)" value={ideaSupply} onChange={e=>setIdeaSupply(e.target.value)} rows={3}
                  style={{width:"100%",border:"none",borderBottom:`2px solid ${C.event}55`,outline:"none",resize:"none",fontSize:14,lineHeight:1.6,color:C.mid,background:"transparent",fontFamily:"'Georgia',serif",padding:"0 0 6px",boxSizing:"border-box"}}/>
              </div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
              <button onClick={()=>setIdeaCat("")} style={{background:!ideaCat?"rgba(0,0,0,0.08)":"transparent",color:!ideaCat?C.dark:C.faint,border:`1.5px solid ${!ideaCat?C.muted:C.border}`,borderRadius:20,padding:"4px 12px",fontSize:12,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>No tag</button>
              {cats.map(cat=>(
                <button key={cat.id} onClick={()=>setIdeaCat(cat.label)} style={{background:ideaCat===cat.label?cat.color:"transparent",color:ideaCat===cat.label?"#fff":cat.color,border:`1.5px solid ${cat.color}`,borderRadius:20,padding:"4px 12px",fontSize:12,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>{cat.label}</button>
              ))}
            </div>
            <div style={{display:"flex",justifyContent:"flex-end"}}>
              <SaveBtn onClick={()=>{addIdea();setShowNewIdeaForm(false);}  } disabled={!ideaText.trim()}>Add idea</SaveBtn>
            </div>
          </div>
          ):(
            <button onClick={()=>setShowNewIdeaForm(true)} style={{width:"100%",background:C.card,border:`1.5px dashed ${C.accent}`,borderRadius:16,padding:"16px 20px",display:"flex",alignItems:"center",justifyContent:"center",gap:10,cursor:"pointer",marginBottom:20,boxShadow:"0 2px 16px rgba(0,0,0,0.05)",transition:"all 0.2s"}}>
              <span style={{fontSize:20,color:C.accent}}>＋</span>
              <span style={{fontSize:15,fontFamily:"'Georgia',serif",color:C.accent}}>New idea</span>
            </button>
          )}

          {ideas.length>0&&(
            <div style={{background:C.card,borderRadius:14,padding:"12px 14px",marginBottom:14,boxShadow:"0 1px 8px rgba(0,0,0,0.05)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <ColHeader>Filters</ColHeader>
                <button onClick={()=>{setIdeaFilter("all");setIdeaCatFilter("all");setIdeaStageFilter("all");}} style={{background:"none",border:"none",fontSize:11,fontFamily:"sans-serif",color:C.faint,cursor:"pointer",textDecoration:"underline",padding:0}}>Reset all</button>
              </div>
              {/* Status */}
              <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:8,flexWrap:"wrap"}}>
                <span style={{fontSize:9,letterSpacing:1,textTransform:"uppercase",fontFamily:"sans-serif",color:C.faint,width:50,flexShrink:0}}>Status</span>
                {[["all","All"],["active","Active"],["done","Done"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setIdeaFilter(v)} style={{background:ideaFilter===v?C.header:"#f4f4f6",color:ideaFilter===v?C.accentText:C.muted,border:"1.5px solid transparent",borderRadius:20,padding:"4px 11px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>{l}</button>
                ))}
                <span style={{marginLeft:"auto",fontSize:11,fontFamily:"sans-serif",color:C.faint}}>{doneCount}/{ideas.length} done</span>
              </div>
              {/* Category */}
              <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:8,flexWrap:"wrap"}}>
                <span style={{fontSize:9,letterSpacing:1,textTransform:"uppercase",fontFamily:"sans-serif",color:C.faint,width:50,flexShrink:0}}>Category</span>
                <button onClick={()=>setIdeaCatFilter("all")} style={{background:ideaCatFilter==="all"?C.header:"#f4f4f6",color:ideaCatFilter==="all"?C.accentText:C.muted,border:"1.5px solid transparent",borderRadius:20,padding:"4px 11px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>All</button>
                {cats.filter(cat=>ideas.some(i=>i.cat===cat.label)).map(cat=>(
                  <button key={cat.id} onClick={()=>setIdeaCatFilter(ideaCatFilter===cat.label?"all":cat.label)}
                    style={{background:ideaCatFilter===cat.label?cat.color:"transparent",color:ideaCatFilter===cat.label?"#fff":cat.color,border:`1.5px solid ${cat.color}`,borderRadius:20,padding:"4px 11px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>{cat.label}</button>
                ))}
              </div>
              {/* Stage */}
              <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                <span style={{fontSize:9,letterSpacing:1,textTransform:"uppercase",fontFamily:"sans-serif",color:C.faint,width:50,flexShrink:0}}>Stage</span>
                <button onClick={()=>setIdeaStageFilter("all")} style={{background:ideaStageFilter==="all"?C.header:"#f4f4f6",color:ideaStageFilter==="all"?C.accentText:C.muted,border:"1.5px solid transparent",borderRadius:20,padding:"4px 11px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>All</button>
                {[["concept","Concept","#b8bcc8"],["progress","In progress","#b58a9b"],["ready","Ready","#7aaa8a"]].map(([v,l,col])=>(
                  <button key={v} onClick={()=>setIdeaStageFilter(ideaStageFilter===v?"all":v)}
                    style={{background:ideaStageFilter===v?col:"transparent",color:ideaStageFilter===v?"#fff":col,border:`1.5px solid ${col}55`,borderRadius:20,padding:"4px 11px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>{l}</button>
                ))}
              </div>
            </div>
          )}

          {filtIdeas.length===0&&<div style={{textAlign:"center",padding:"40px 20px"}}><p style={{color:C.faint,fontStyle:"italic",fontSize:15,margin:0}}>{ideas.length===0?"No ideas yet — add your first one above!":"Nothing to show here."}</p></div>}

          {/* Pinned section */}
          {pinnedFilt.length>0&&<p style={{margin:"0 0 8px 4px",fontSize:9,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"sans-serif",color:C.muted}}>📌 Pinned</p>}
          {pinnedFilt.map(idea=><IdeaCard key={idea.id} idea={idea} cats={cats} expandedIdeas={expandedIdeas} toggleIdeaExpand={toggleIdeaExpand} toggleIdea={toggleIdea} pinnedIdeas={pinnedIdeas} toggleIdeaPin={toggleIdeaPin} setIdeas={setIdeas} setSupplies={setSupplies} ideaTodos={ideaTodos} ideaStages={ideaStages} advanceIdeaStage={advanceIdeaStage} IDEA_STAGE_CFG={IDEA_STAGE_CFG} addIdeaToTodo={addIdeaToTodo} removeIdeaFromTodo={removeIdeaFromTodo} reUseIdea={reUseIdea} schedulingId={schedulingId} setSchedulingId={setSchedulingId} schedWeekId={schedWeekId} setSchedWeekId={setSchedWeekId} weeks={weeks} commitIdea={commitIdea} delIdea={delIdea} C={C} ColHeader={ColHeader} InlineEdit={InlineEdit} CancelBtn={CancelBtn} formatWeekLabel={formatWeekLabel}/>)}

          {/* Ideas section */}
          {unpinnedFilt.length>0&&pinnedFilt.length>0&&<p style={{margin:"12px 0 8px 4px",fontSize:9,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"sans-serif",color:C.muted}}>Ideas</p>}
          {unpinnedFilt.map(idea=><IdeaCard key={idea.id} idea={idea} cats={cats} expandedIdeas={expandedIdeas} toggleIdeaExpand={toggleIdeaExpand} toggleIdea={toggleIdea} pinnedIdeas={pinnedIdeas} toggleIdeaPin={toggleIdeaPin} setIdeas={setIdeas} setSupplies={setSupplies} ideaTodos={ideaTodos} ideaStages={ideaStages} advanceIdeaStage={advanceIdeaStage} IDEA_STAGE_CFG={IDEA_STAGE_CFG} addIdeaToTodo={addIdeaToTodo} removeIdeaFromTodo={removeIdeaFromTodo} reUseIdea={reUseIdea} schedulingId={schedulingId} setSchedulingId={setSchedulingId} schedWeekId={schedWeekId} setSchedWeekId={setSchedWeekId} weeks={weeks} commitIdea={commitIdea} delIdea={delIdea} C={C} ColHeader={ColHeader} InlineEdit={InlineEdit} CancelBtn={CancelBtn} formatWeekLabel={formatWeekLabel}/>)}
        </div>
      )}

      {/* ══ TO-DO TAB ══ */}
      {tab==="todo"&&(
        <div style={{maxWidth:680,margin:"0 auto",padding:"24px 16px 40px"}}>
          {/* Manual add */}
          <div style={{background:C.card,borderRadius:16,padding:20,marginBottom:20,boxShadow:"0 2px 16px rgba(0,0,0,0.07)"}}>
            <ColHeader style={{marginBottom:12}}>Add item</ColHeader>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <input type="text" placeholder="Something you need to do..." value={newTodoText} onChange={e=>setNewTodoText(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")addManualTodo();}}
                style={{flex:1,border:"none",borderBottom:`2px solid ${C.border}`,outline:"none",fontSize:16,color:C.dark,background:"transparent",fontFamily:"'Georgia',serif",padding:"6px 0",boxSizing:"border-box"}}/>
              <button onClick={addManualTodo} disabled={!newTodoText.trim()} style={{background:newTodoText.trim()?C.header:C.border,color:newTodoText.trim()?C.accentText:C.faint,border:"none",borderRadius:10,padding:"10px 20px",fontSize:14,fontFamily:"sans-serif",cursor:newTodoText.trim()?"pointer":"default",whiteSpace:"nowrap"}}>+ Add</button>
            </div>
          </div>

          {/* View + filter */}
          {totalTodos>0&&(
            <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
              <div style={{display:"flex",gap:4,background:C.card,borderRadius:20,padding:3,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                <button onClick={()=>setTodoViewMode("type")} style={{background:todoViewMode==="type"?C.header:"transparent",color:todoViewMode==="type"?C.accentText:C.muted,border:"none",borderRadius:16,padding:"4px 12px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>By source</button>
                <button onClick={()=>setTodoViewMode("week")} style={{background:todoViewMode==="week"?C.header:"transparent",color:todoViewMode==="week"?C.accentText:C.muted,border:"none",borderRadius:16,padding:"4px 12px",fontSize:11,fontFamily:"sans-serif",cursor:"pointer",transition:"all 0.15s"}}>By week</button>
              </div>
              {[["active","Active"],["all","All"],["done","Done"]].map(([v,l])=>(
                <button key={v} onClick={()=>setTodoFilter(v)} style={{background:todoFilter===v?C.accent:C.card,color:todoFilter===v?"#fff":C.muted,border:"none",borderRadius:20,padding:"6px 14px",fontSize:12,fontFamily:"sans-serif",cursor:"pointer",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",transition:"all 0.15s"}}>{l}</button>
              ))}
              <span style={{marginLeft:"auto",fontSize:12,fontFamily:"sans-serif",color:C.faint}}>{totalActive} active</span>
            </div>
          )}

          {/* By week view */}
          {todoViewMode==="week"&&(
            <div>
              {todosByWeek().length===0&&<div style={{textAlign:"center",padding:"40px 20px"}}><p style={{color:C.faint,fontStyle:"italic",fontSize:15,margin:0}}>No video prep todos yet — add prep notes to your videos on the Planning Board!</p></div>}
              {todosByWeek().map((group,gi)=>(
                <div key={gi} style={{marginBottom:24}}>
                  <div style={{background:C.header,borderRadius:"12px 12px 0 0",padding:"10px 16px"}}>
                    <p style={{margin:0,fontSize:10,letterSpacing:2,textTransform:"uppercase",fontFamily:"sans-serif",color:C.accentDim}}>Week of</p>
                    <p style={{margin:"2px 0 0",fontSize:15,color:C.accentText,fontWeight:"normal"}}>{group.label}</p>
                  </div>
                  <div style={{background:C.card,borderRadius:"0 0 12px 12px",overflow:"hidden",boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>
                    {group.items.map(item=>(
                      <div key={item.entryId} style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"flex-start",gap:14,background:item.done?`${C.highlightSoft}88`:"transparent",transition:"all 0.2s"}}>
                        <button onClick={()=>togglePrepDone(item.weekId,item.entryId)} style={{width:24,height:24,borderRadius:"50%",flexShrink:0,marginTop:1,border:`2px solid ${item.done?C.accent:C.border}`,background:item.done?C.accent:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.2s"}}>
                          {item.done&&<span style={{color:"#fff",fontSize:11,lineHeight:1}}>✓</span>}
                        </button>
                        <div style={{flex:1}}>
                          <p style={{margin:0,fontSize:14,color:item.done?C.faint:C.dark,fontWeight:"bold",textDecoration:item.done?"line-through":"none",textDecorationColor:C.accent}}>{item.videoName||"Untitled"}</p>
                          <p style={{margin:"3px 0 0",fontSize:12,color:item.done?C.faint:C.mid,whiteSpace:"pre-wrap"}}>{item.prep}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {todoViewMode==="type"&&<div>

          {/* Video prep section */}
          {filtScheduled.length>0&&(
            <div style={{marginBottom:24}}>
              <ColHeader style={{margin:"0 0 10px 4px"}}>Video prep · {filtScheduled.length}</ColHeader>
              {filtScheduled.map(item=>(
                <div key={item.entryId} style={{background:item.done?`${C.highlightSoft}88`:C.card,borderRadius:14,marginBottom:8,padding:"14px 16px",display:"flex",alignItems:"flex-start",gap:14,boxShadow:"0 1px 8px rgba(0,0,0,0.05)",border:`1.5px solid ${item.done?C.border:"transparent"}`,transition:"all 0.2s"}}>
                  <button onClick={()=>togglePrepDone(item.weekId,item.entryId)} style={{width:26,height:26,borderRadius:"50%",flexShrink:0,marginTop:1,border:`2px solid ${item.done?C.accent:C.border}`,background:item.done?C.accent:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.2s"}}>
                    {item.done&&<span style={{color:"#fff",fontSize:13,lineHeight:1}}>✓</span>}
                  </button>
                  <div style={{flex:1}}>
                    <p style={{margin:0,fontSize:15,color:item.done?C.faint:C.dark,fontWeight:"bold",textDecoration:item.done?"line-through":"none",textDecorationColor:C.accent,textDecorationThickness:"2px"}}>{item.videoName||"Untitled"}</p>
                    <p style={{margin:"4px 0 0",fontSize:13,color:item.done?C.faint:C.mid,whiteSpace:"pre-wrap"}}>{item.prep}</p>
                    <span style={{display:"inline-block",marginTop:6,fontSize:10,fontFamily:"sans-serif",color:C.faint,background:`${C.accent}12`,padding:"2px 8px",borderRadius:8}}>{item.weekLabel}</span>
                  </div>
                  {item.done&&<button onClick={()=>delEntry(item.weekId,item.entryId)} style={{background:"none",border:"none",color:C.faint,fontSize:18,cursor:"pointer",padding:2,lineHeight:1,flexShrink:0}}>×</button>}
                </div>
              ))}
            </div>
          )}

          {/* Idea todos */}
          {ideaTodoItems.length>0&&(todoFilter==="all"||todoFilter==="active")&&(
            <div style={{marginBottom:24}}>
              <ColHeader style={{margin:"0 0 10px 4px"}}>From ideas · {ideaTodoItems.length}</ColHeader>
              {ideaTodoItems.map(idea=>{
                const cat=cats.find(c=>c.label===idea.cat)||cats[cats.length-1]||{color:C.muted};
                return(
                  <div key={idea.id} style={{background:C.card,borderRadius:14,marginBottom:8,padding:"14px 16px",display:"flex",alignItems:"flex-start",gap:14,boxShadow:"0 1px 8px rgba(0,0,0,0.05)"}}>
                    <div style={{flex:1}}>
                      <p style={{margin:0,fontSize:15,color:C.dark,fontWeight:"bold"}}>{idea.text}</p>
                      {idea.prep&&<p style={{margin:"4px 0 0",fontSize:13,color:C.mid}}>{idea.prep}</p>}
                      <span style={{display:"inline-block",marginTop:6,fontSize:10,fontFamily:"sans-serif",background:cat.color+"20",color:cat.color,padding:"2px 8px",borderRadius:8}}>{idea.cat}</span>
                    </div>
                    <button onClick={()=>removeIdeaFromTodo(idea.id)} style={{background:"none",border:"none",color:C.faint,fontSize:18,cursor:"pointer",padding:2,lineHeight:1,flexShrink:0}}>×</button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Manual todos */}
          {filtManual.length>0&&(
            <div style={{marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",margin:"0 0 10px 4px"}}>
                <ColHeader>Other · {filtManual.length}</ColHeader>
                {hasDoneManual&&todoFilter!=="active"&&<button onClick={clearDoneTodos} style={{background:"none",border:"none",fontSize:12,fontFamily:"sans-serif",color:C.muted,cursor:"pointer"}}>Clear completed</button>}
              </div>
              {filtManual.map(t=>(
                <div key={t.id} style={{background:t.done?`${C.highlightSoft}88`:C.card,borderRadius:14,marginBottom:8,padding:"14px 16px",display:"flex",alignItems:"center",gap:14,boxShadow:"0 1px 8px rgba(0,0,0,0.05)",border:`1.5px solid ${t.done?C.border:"transparent"}`,transition:"all 0.2s"}}>
                  <button onClick={()=>toggleTodo(t.id)} style={{width:26,height:26,borderRadius:"50%",flexShrink:0,border:`2px solid ${t.done?C.accent:C.border}`,background:t.done?C.accent:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.2s"}}>
                    {t.done&&<span style={{color:"#fff",fontSize:13,lineHeight:1}}>✓</span>}
                  </button>
                  <InlineEdit value={t.text} onChange={v=>setManualTodos(p=>p.map(x=>x.id===t.id?{...x,text:v}:x))} style={{flex:1,fontSize:16,color:t.done?C.faint:C.dark,textDecoration:t.done?"line-through":"none",textDecorationColor:C.accent,textDecorationThickness:"2px",display:"block"}}/>
                  <button onClick={()=>delTodo(t.id)} style={{background:"none",border:"none",color:C.faint,fontSize:18,cursor:"pointer",padding:2,lineHeight:1}}>×</button>
                </div>
              ))}
            </div>
          )}

          {totalTodos===0&&<div style={{textAlign:"center",padding:"40px 20px"}}><p style={{color:C.faint,fontStyle:"italic",fontSize:15,margin:0}}>Nothing to do yet — add something above or schedule a video!</p></div>}
          </div>}
        </div>
      )}

      {/* ══ SUPPLIES TAB ══ */}
      {tab==="supplies"&&(
        <div style={{maxWidth:680,margin:"0 auto",padding:"24px 16px 40px"}}>
          <div style={{background:C.card,borderRadius:16,padding:20,marginBottom:20,boxShadow:"0 2px 16px rgba(0,0,0,0.07)"}}>
            <ColHeader style={{marginBottom:12}}>Add supply</ColHeader>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <input ref={supplyRef} type="text" placeholder="e.g. fresh chalk, dye..." value={supplyText} onChange={e=>setSupplyText(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")addSupply();}}
                style={{flex:1,border:"none",borderBottom:`2px solid ${C.border}`,outline:"none",fontSize:16,color:C.dark,background:"transparent",fontFamily:"'Georgia',serif",padding:"6px 0",boxSizing:"border-box"}}/>
              <button onClick={addSupply} disabled={!supplyText.trim()} style={{background:supplyText.trim()?C.header:C.border,color:supplyText.trim()?C.accentText:C.faint,border:"none",borderRadius:10,padding:"10px 20px",fontSize:14,fontFamily:"sans-serif",cursor:supplyText.trim()?"pointer":"default",whiteSpace:"nowrap"}}>+ Add</button>
            </div>
          </div>
          {needed.length>0&&(
            <div style={{marginBottom:24}}>
              <ColHeader style={{margin:"0 0 10px 4px"}}>Need to buy · {needed.length}</ColHeader>
              {needed.map(s=>(
                <div key={s.id} style={{background:C.card,borderRadius:14,marginBottom:8,padding:"14px 16px",display:"flex",alignItems:"center",gap:14,boxShadow:"0 1px 8px rgba(0,0,0,0.05)",border:"1.5px solid transparent"}}>
                  <button onClick={()=>toggleSupply(s.id)} style={{width:26,height:26,borderRadius:"50%",flexShrink:0,border:`2px solid ${C.border}`,background:"transparent",cursor:"pointer"}}/>
                  <div style={{flex:1}}>
                    <InlineEdit value={s.text} onChange={v=>setSupplies(p=>p.map(x=>x.id===s.id?{...x,text:v}:x))}
                      style={{fontSize:16,color:C.dark,lineHeight:1.4,display:"block"}}/>
                    {s.sourceIdea&&<p style={{margin:"2px 0 0",fontSize:11,fontFamily:"sans-serif",color:C.faint}}>({s.sourceIdea})</p>}
                  </div>
                  <button onClick={()=>delSupply(s.id)} style={{background:"none",border:"none",color:C.faint,fontSize:18,cursor:"pointer",padding:2,lineHeight:1}}>×</button>
                </div>
              ))}
            </div>
          )}
          {purchased.length>0&&(
            <div>
              <ColHeader style={{margin:"0 0 10px 4px"}}>Purchased · {purchased.length}</ColHeader>
              {purchased.map(s=>(
                <div key={s.id} style={{background:C.highlightSoft,borderRadius:14,marginBottom:8,padding:"14px 16px",display:"flex",alignItems:"center",gap:14,border:`1.5px solid ${C.border}`}}>
                  <button onClick={()=>toggleSupply(s.id)} style={{width:26,height:26,borderRadius:"50%",flexShrink:0,border:`2px solid ${C.accent}`,background:C.accent,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <span style={{color:"#fff",fontSize:13,lineHeight:1}}>✓</span>
                  </button>
                  <div style={{flex:1}}>
                    <p style={{margin:0,fontSize:16,color:C.faint,lineHeight:1.4,textDecoration:"line-through",textDecorationColor:C.accent,textDecorationThickness:"2px"}}>{s.text}</p>
                    {s.sourceIdea&&<p style={{margin:"2px 0 0",fontSize:11,fontFamily:"sans-serif",color:C.faint}}>({s.sourceIdea})</p>}
                  </div>
                  <button onClick={()=>delSupply(s.id)} style={{background:"none",border:"none",color:C.faint,fontSize:18,cursor:"pointer",padding:2,lineHeight:1}}>×</button>
                </div>
              ))}
            </div>
          )}
          {supplies.length===0&&<div style={{textAlign:"center",padding:"40px 20px"}}><p style={{color:C.faint,fontStyle:"italic",fontSize:15,margin:0}}>No supplies listed yet — add something above!</p></div>}
        </div>
      )}

      {/* ══ NOTES TAB ══ */}
      {tab==="notes"&&(
        <div style={{maxWidth:680,margin:"0 auto",padding:"24px 16px 40px"}}>
          <div style={{background:C.card,borderRadius:16,padding:20,boxShadow:"0 2px 16px rgba(0,0,0,0.07)",minHeight:"60vh"}}>
            <ColHeader style={{marginBottom:14}}>Notes</ColHeader>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)}
              placeholder="This is your space — jot down observations, ideas, things you noticed, anything on your mind..."
              style={{width:"100%",minHeight:"55vh",border:"none",outline:"none",resize:"none",fontSize:16,lineHeight:1.8,color:C.dark,background:"transparent",fontFamily:"'Georgia',serif",padding:0,boxSizing:"border-box"}}/>
          </div>
          <p style={{textAlign:"center",fontSize:11,fontFamily:"sans-serif",color:C.faint,marginTop:10}}>Auto-saves as you type</p>
        </div>
      )}

      {/* New week FAB */}
      {tab==="board"&&(
        <div style={{position:"fixed",bottom:0,left:0,right:0,padding:"16px 24px 28px",background:`linear-gradient(to top, ${C.page} 60%, transparent)`,display:"flex",justifyContent:"center"}}>
          <button onClick={addWeek} style={{background:C.accent,color:"#fff",border:"none",borderRadius:50,padding:"14px 36px",fontSize:16,fontFamily:"sans-serif",letterSpacing:0.5,cursor:"pointer",boxShadow:`0 4px 20px ${C.accent}55`}}>+ New week</button>
        </div>
      )}

      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
